import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Papa from 'papaparse';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useBulkImport } from '@/context/BulkImportContext';
import { useSingleImport } from '@/context/SingleImportContext';
import { supabase } from '@/lib/supabaseClient';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { autoCategorize, isAtmWithdrawalDescription, isRefundDescription } from '@/lib/categorize';
import TAXONOMY from '@/lib/taxonomyExpense';
import { CategorySelect } from '@/components/CategorySelect';
import { detectCurrencyFromCsv } from '@/lib/csvCurrencyDetect';
import { formatMoney, todayISO, CURRENCIES } from '@/lib/format';
import { prettifyDescription, modeLabel, suggestPatternFromDescription } from '@/lib/txDescription';
import { effectivePlan, hasFeature, isOnTrial } from '@/lib/entitlements';
import { sha256OfFile, checkDuplicate } from '@/lib/fileHash';
import { syncAccountBalanceFromStatement } from '@/lib/accountBalance';
import { insertTransactionsBulkIdempotent } from '@/lib/insertTransaction';
import { detectInstallmentConversions, computeClusterPreset } from '@/lib/installmentDetector';
import { InstallmentPlanCard } from '@/components/imports/InstallmentPlanCard';
import { toast } from 'sonner';
import { UploadCloud, CheckCircle2, Sparkles, FileText, RotateCcw, FileType2, Brain, Layers, Files, X, Loader2, AlertTriangle, ArrowRight, Bell, Wallet, Plus, Info, Shield, Check } from 'lucide-react';
import StoredDocumentsPanel from '@/components/StoredDocumentsPanel';
import LearnRuleDialog from '@/components/imports/LearnRuleDialog';
import { Progress } from '@/components/ui/progress';
import usePageTitle from '@/hooks/usePageTitle';
import { useParseJobs } from '@/hooks/useParseJobs';
import StatementAccountDialog, { mapAccountTypeForFE } from '@/components/StatementAccountDialog';
import LockedPdfDialog from '@/components/LockedPdfDialog';
import OnboardingPoolWelcomeBanner from '@/components/OnboardingPoolWelcomeBanner';
import { isPdfEncrypted } from '@/lib/pdfEncryption';
// Phase 84.3 slice-1 — internal components extracted to their own files.
// Behavior is unchanged; each import maps to a former internal function
// in this file. See /app/frontend/src/components/import/statements/.
import StatusBadge from '@/components/import/statements/StatusBadge';
import RowAccountPicker from '@/components/import/statements/RowAccountPicker';
import AccountPickerCard from '@/components/import/statements/AccountPickerCard';
import NewAccountInlineDialog from '@/components/import/statements/NewAccountInlineDialog';
import DuplicateWarningDialog from '@/components/import/statements/DuplicateWarningDialog';
import DebtReconcileSummary from '@/components/liabilities/DebtReconcileSummary';
import { jobState, importedCount } from '@/lib/importStatus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Phase 71.2 — fire-and-forget call to the recurring auto-confirm endpoint.
// Called after every successful import so any active recurring rule whose
// statement evidence arrived in this import has its `next_due` quietly
// rolled forward. Surfaces a calm toast like "2 recurring rules confirmed"
// when at least one rolls; stays silent otherwise (no noise on imports
// that don't touch recurring rules).
//
// Idempotent on the backend: PDF imports also kick off the same scan via
// the `mark-consumed` background task, so duplicate calls are harmless.
async function triggerRecurringAutoConfirm(session) {
  try {
    const token = session?.access_token;
    if (!token) return;
    const r = await axios.post(
      `${BACKEND_URL}/api/recurring/auto-confirm`,
      {},
      { headers: { Authorization: `Bearer ${token}` }, timeout: 12000 },
    );
    const confirmed = r?.data?.confirmed || [];
    if (confirmed.length === 0) return;
    // Compose a single toast naming the first 2 rules; collapse the rest.
    const names = confirmed.slice(0, 2).map((c) => c.rule_name).filter(Boolean);
    const extra = confirmed.length - names.length;
    let summary;
    if (confirmed.length === 1) {
      summary = `Recurring confirmed by your import: ${names[0]}.`;
    } else if (extra > 0) {
      summary = `${confirmed.length} recurring rules confirmed by your import: ${names.join(', ')} +${extra} more.`;
    } else {
      summary = `${confirmed.length} recurring rules confirmed by your import: ${names.join(', ')}.`;
    }
    toast.success(summary, { duration: 9000 });
  } catch {
    // Silent on failure — auto-confirm is a polish layer; users can
    // always Mark Paid manually if a rule didn't roll forward.
  }
}

// Calm relative-time formatter used by the "already imported" empty state.
function relTimeShort(iso) {
  if (!iso) return 'just now';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return 'a moment ago';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

const FIELD_OPTIONS = [
  { value: 'date', label: 'Date' },
  { value: 'description', label: 'Description / Merchant' },
  { value: 'amount', label: 'Amount (signed)' },
  { value: 'debit', label: 'Debit (outflow)' },
  { value: 'credit', label: 'Credit (inflow)' },
  { value: 'category', label: 'Category' },
  { value: '__skip', label: '  Skip column' },
];

// Derived from the ONE taxonomy registry (shared/taxonomy/expense_categories.v1.json),
// never hand-listed: a hard-coded copy silently went stale and dropped
// Car Maintenance, Transfers and Investments from this picker.
const CATEGORY_CHOICES = TAXONOMY.categories.map((c) => c.id);

// Priority-order-aware column guesser. Called once per header cell — we
// return the best single guess for that cell. Ambiguity (e.g. Vanguard has
// both "Trade Date" and "Settlement Date") is resolved by scoring: we
// return the most specific guess first so the caller can pick a winner
// when multiple columns claim the same role. Callers may still override
// in the Map Columns UI.
function guessMapping(header) {
  const h = (header || '').toLowerCase().trim();
  // Date signals — prefer the *economic* date over the *settlement* date.
  //   Trade Date / Transaction Date > Posted Date > Settlement Date > Date
  if (/^trade date|^transaction date|^txn date/.test(h)) return 'date';
  if (/^post(ed|ing)( date)?$/.test(h) || /\bposted date\b/.test(h)) return 'date';
  if (h === 'date' || /^value date/.test(h)) return 'date';
  if (/settlement/.test(h)) return 'date_secondary'; // second-class date match
  if (/(date|posted|txn)/.test(h)) return 'date';
  if (/(desc|narration|particular|merchant|details|memo)/.test(h)) return 'description';
  if (/(debit|withdraw|out)/.test(h)) return 'debit';
  if (/(credit|deposit|in)/.test(h)) return 'credit';
  // Amount signals — prefer Net Amount (includes fees) over Principal Amount.
  if (/^net amount/.test(h) || h === 'net amount') return 'amount';
  if (/^principal amount/.test(h)) return 'amount_secondary';
  if (/amount|amt/.test(h)) return 'amount';
  if (/category|tag/.test(h)) return 'category';
  return '__skip';
}

// Post-process: when guessMapping returns *_secondary we treat it as
// 'date' or 'amount' only if no primary was found for that role. This
// keeps Trade Date beating Settlement Date without dropping the CSV on
// the floor when only Settlement Date is present.
function resolveSecondaryGuesses(guesses) {
  const roles = { date: -1, amount: -1 };
  guesses.forEach((g, i) => { if (g === 'date' && roles.date === -1) roles.date = i; });
  guesses.forEach((g, i) => { if (g === 'amount' && roles.amount === -1) roles.amount = i; });
  return guesses.map((g) => {
    if (g === 'date_secondary')   return roles.date   === -1 ? 'date'   : '__skip';
    if (g === 'amount_secondary') return roles.amount === -1 ? 'amount' : '__skip';
    return g;
  });
}

// ---------------------------------------------------------------------------
// Phase 71C — date parsing for CSV imports
//
// Previously this parser assumed DD/MM/YYYY universally — fine for Indian
// statements (the original target market) but it silently corrupted dates
// from US statements (MM/DD/YYYY), producing nonsense like "2026-29-05"
// which Postgres then rejected with "date/time field value out of range".
//
// The fix: a format-aware parser plus an auto-detector that scans the
// whole date column for an unambiguous signal (any first-segment > 12
// proves DMY; any second-segment > 12 proves MDY). If the file is
// genuinely ambiguous, we fall back to the user-supplied format, then
// finally to DMY (preserves the original behaviour for old India CSVs).
// ---------------------------------------------------------------------------

const DATE_FMT_AUTO = 'auto';
const DATE_FMT_MDY  = 'mdy'; // US — MM/DD/YYYY (or MM-DD-YYYY)
const DATE_FMT_DMY  = 'dmy'; // IN/EU — DD/MM/YYYY (or DD-MM-YYYY)
const DATE_FMT_YMD  = 'ymd'; // ISO  — YYYY-MM-DD (or YYYY/MM/DD)

function detectDateFormat(values) {
  // Returns one of DATE_FMT_MDY | DATE_FMT_DMY | DATE_FMT_YMD | 'ambiguous'
  // by scanning every value in the column. The moment we find an
  // unambiguous signal we return immediately.
  if (!Array.isArray(values) || values.length === 0) return 'ambiguous';
  let sawYmd = 0;
  for (const raw of values) {
    if (raw === null || raw === undefined) continue;
    const t = String(raw).trim();
    if (!t) continue;
    // ISO-ish: starts with 4-digit year.
    if (/^\d{4}[/\-]\d{1,2}[/\-]\d{1,2}/.test(t)) { sawYmd += 1; continue; }
    const m = t.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{2,4})/);
    if (!m) continue;
    const a = parseInt(m[1], 10);
    const b = parseInt(m[2], 10);
    if (a > 12) return DATE_FMT_DMY;  // first slot can't be a month → DMY
    if (b > 12) return DATE_FMT_MDY;  // second slot can't be a month → MDY
  }
  if (sawYmd > 0) return DATE_FMT_YMD;
  return 'ambiguous';
}

function parseDateLoose(s, fmt = DATE_FMT_AUTO) {
  if (!s) return todayISO();
  const t = String(s).trim();
  // ISO-ish always parses first — no ambiguity possible.
  if (/^\d{4}[/\-]\d{1,2}[/\-]\d{1,2}/.test(t)) {
    const m = t.match(/^(\d{4})[/\-](\d{1,2})[/\-](\d{1,2})/);
    if (m) {
      const y = m[1];
      let mo = m[2].padStart(2, '0');
      let d = m[3].padStart(2, '0');
      // Hardening guard: some exports emit YYYY-DD-MM (non-standard) and
      // we'd otherwise hand Postgres a value like "2026-29-05" which
      // crashes the import. If month > 12 and day <= 12, swap them so
      // we produce a valid ISO date. If still invalid (both > 12), fall
      // back to today rather than letting an unparseable value through.
      const mInt = parseInt(mo, 10);
      const dInt = parseInt(d, 10);
      if (mInt > 12 && dInt <= 12) {
        const tmp = mo; mo = d; d = tmp;
      } else if (mInt < 1 || mInt > 12 || dInt < 1 || dInt > 31) {
        // eslint-disable-next-line no-console
        console.warn('[parseDateLoose] unparseable ISO-ish date:', t, '→ using today');
        return todayISO();
      }
      return `${y}-${mo}-${d}`;
    }
    return t.slice(0, 10);
  }
  const m1 = t.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{2,4})/);
  if (m1) {
    let a = m1[1];
    let b = m1[2];
    let y = m1[3];
    if (y.length === 2) y = '20' + y;
    // Resolve which slot is the month based on the chosen format. Default
    // remains DMY so any importer that hasn't been told otherwise behaves
    // exactly as before.
    let day; let month;
    const useFmt = fmt === DATE_FMT_AUTO ? DATE_FMT_DMY : fmt;
    if (useFmt === DATE_FMT_MDY) { month = a; day = b; }
    else                         { day = a; month = b; }
    // Final guard: if the chosen format produced a month > 12 (e.g.
    // user picked DMY by mistake on a US file), flip it. Better to
    // return a likely-correct date than to throw at the DB.
    if (parseInt(month, 10) > 12 && parseInt(day, 10) <= 12) {
      const tmp = month; month = day; day = tmp;
    }
    // Final hardening: validate range. If still out of bounds, fall back
    // to today rather than letting an invalid value through.
    const mInt = parseInt(month, 10);
    const dInt = parseInt(day, 10);
    if (mInt < 1 || mInt > 12 || dInt < 1 || dInt > 31) {
      // eslint-disable-next-line no-console
      console.warn('[parseDateLoose] unparseable date:', t, '→ using today');
      return todayISO();
    }
    return `${y}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  const d = new Date(t);
  if (!isNaN(d)) return d.toISOString().slice(0, 10);
  return todayISO();
}

// ---------------------------------------------------------------------------
// CSV header sniffer (Phase 71.0)
// ---------------------------------------------------------------------------
// Some banks (Fidelity, Vanguard, a few Indian credit cards) prepend a few
// rows of marketing/plan-name/account-context preamble before the actual
// transaction table starts. Papa.parse with `header: true` will then treat
// that preamble row as headers and the import is hopelessly garbled.
//
// We solve this with a two-pass parse: first scan up to the first 15 rows
// with `header: false`, pick the row that contains the most CSV-header
// keywords (≥ MIN_KEYWORDS), and re-parse using everything below that as
// the data body. If no row scores above the threshold we fall back to the
// default behaviour so we never make things worse.
const CSV_HEADER_KEYWORDS = [
  'date', 'transaction date', 'posted', 'posting date', 'value date',
  'description', 'narration', 'particulars', 'details', 'payee',
  'merchant', 'reference',
  'amount', 'value', 'debit', 'credit', 'withdrawal', 'deposit',
  'type', 'transaction type', 'category', 'balance', 'running balance',
  // Investment-style columns that should be treated as holdings, NOT
  // transaction data (see detectHoldingsSnapshot below).
  'symbol', 'ticker', 'quantity', 'shares', 'units', 'price', 'market value',
  'cost basis',
];

function countHeaderKeywordHits(row) {
  if (!row) return 0;
  let hits = 0;
  for (const cell of row) {
    if (cell === null || cell === undefined) continue;
    const norm = String(cell).trim().toLowerCase();
    if (!norm) continue;
    if (CSV_HEADER_KEYWORDS.some((k) => norm === k || norm.includes(k))) {
      hits += 1;
    }
  }
  return hits;
}

/** Returns { headerRowIndex, headerRow } or null when no good header found. */
function detectHeaderRow(allRows, { minKeywords = 3, scanDepth = 15 } = {}) {
  if (!Array.isArray(allRows) || allRows.length === 0) return null;
  let best = null;
  const limit = Math.min(scanDepth, allRows.length);
  for (let i = 0; i < limit; i += 1) {
    const r = allRows[i];
    if (!Array.isArray(r)) continue;
    const hits = countHeaderKeywordHits(r);
    if (hits >= minKeywords && (best === null || hits > best.hits)) {
      best = { headerRowIndex: i, headerRow: r, hits };
    }
  }
  return best;
}

// Phase 73 hotfix — multi-section CSV support (Vanguard OfxDownload):
//   Some brokerage exports pack **two tables into one CSV**: a holdings
//   snapshot at the top and a transaction history below. The naive
//   detector (above) locks onto the first header and bails out with
//   "holdings snapshot" — even though the real transactions are 25 rows
//   further down. This helper scans the *entire* file and returns every
//   candidate header row so the caller can pick the transaction section.
function detectAllHeaderRows(allRows, { minKeywords = 3 } = {}) {
  if (!Array.isArray(allRows) || allRows.length === 0) return [];
  const candidates = [];
  for (let i = 0; i < allRows.length; i += 1) {
    const r = allRows[i];
    if (!Array.isArray(r)) continue;
    const hits = countHeaderKeywordHits(r);
    if (hits >= minKeywords) {
      candidates.push({ headerRowIndex: i, headerRow: r, hits });
    }
  }
  return candidates;
}

// Heuristic: if the header columns look like portfolio positions (Symbol/
// Quantity/Market Value/etc.) and contain *no* transaction-shaped columns
// (Date/Amount/Description), treat the file as a holdings snapshot. We
// surface a clear banner instead of importing thousands of position rows
// as if they were transactions.
const HOLDINGS_ONLY_KEYS  = ['symbol', 'ticker', 'shares', 'units', 'quantity', 'market value', 'cost basis', 'price'];
const TXN_REQUIRED_KEYS   = ['date', 'amount', 'description', 'debit', 'credit', 'withdrawal', 'deposit', 'narration', 'particulars'];

function detectHoldingsSnapshot(headerRow) {
  if (!Array.isArray(headerRow)) return false;
  const lowered = headerRow.map((c) => String(c || '').trim().toLowerCase());
  const hasHoldings = lowered.some((c) => HOLDINGS_ONLY_KEYS.some((k) => c.includes(k)));
  const hasTxn      = lowered.some((c) => TXN_REQUIRED_KEYS.some((k) => c === k || c.includes(k)));
  return hasHoldings && !hasTxn;
}

function parseAmount(v) {
  if (v === null || v === undefined) return 0;
  const cleaned = String(v).replace(/[,\s₹$£€]/g, '').replace(/[A-Za-z]/g, '');
  const n = Number(cleaned);
  return isNaN(n) ? 0 : n;
}

/** Suggest a learn-pattern from a description. Delegates to the shared
 *  txDescription helper so it stays consistent with how the Transactions
 *  list prettifies the same strings. The legacy heuristic below is kept
 *  only as a final fallback. */
function suggestPattern(desc) {
  const smart = suggestPatternFromDescription(desc);
  if (smart) return smart;
  // ---- Legacy fallback (pre-Phase 54 hotfix) ----
  if (!desc) return '';
  const tokens = String(desc).toUpperCase().split(/[^A-Z0-9]+/).filter((t) => t.length >= 4);
  const generic = new Set(['UPI', 'NEFT', 'IMPS', 'RTGS', 'POS', 'ATM', 'TRANSFER', 'PAYMENT', 'DEBIT', 'CREDIT', 'BANK', 'ONLINE', 'TXN', 'REF', 'BLR', 'PAYU', 'HDFC', 'ICICI', 'AXIS', 'SBI']);
  const distinctive = tokens.find((t) => !generic.has(t) && !/^\d+$/.test(t));
  return distinctive || tokens[0] || String(desc).slice(0, 12);
}

// ---------------------------------------------------------------------------
// Phase 41, match a parser-extracted account against the user's existing
// accounts. Confirmed product decisions:
//   1) Strong match:   last4 + currency   (most distinctive identifier)
//   2) Fallback match: bank_name fuzzy-match in `name` + type compatibility
//
// Returns the matched account row or null. Never throws.
// ---------------------------------------------------------------------------
function findMatchingAccount(parsed, accounts) {
  if (!parsed || !Array.isArray(accounts) || accounts.length === 0) return null;
  const parsedLast4 = (parsed.last4 || '').trim();
  const parsedCurrency = (parsed.currency || '').toUpperCase().trim();
  const parsedBank = (parsed.bank_name || '').toLowerCase().trim();
  const parsedTypeFE = mapAccountTypeForFE(parsed.account_type); // bank|credit_card|demat

  // Tier 1, last4 + currency
  if (parsedLast4) {
    const tier1 = accounts.find((a) => {
      if ((a.last4 || '').trim() !== parsedLast4) return false;
      // currency must match if we have one on both sides; else treat as ok
      if (parsedCurrency && a.currency && a.currency.toUpperCase() !== parsedCurrency) return false;
      return true;
    });
    if (tier1) return tier1;
  }

  // Tier 2, bank_name fuzzy substring + type compatibility
  if (parsedBank) {
    const tier2 = accounts.find((a) => {
      const accountName = (a.name || '').toLowerCase();
      if (!accountName.includes(parsedBank) && !parsedBank.includes(accountName)) {
        // Try first significant token (e.g. "HDFC" inside "HDFC Bank")
        const firstTok = parsedBank.split(/\s+/)[0];
        if (!firstTok || firstTok.length < 3 || !accountName.includes(firstTok)) return false;
      }
      // type compatibility: if parsed says credit_card, only match credit_card
      // accounts; if parsed says bank, only match bank-like accounts.
      const aType = (a.type || '').toLowerCase();
      if (parsedTypeFE === 'credit_card' && aType !== 'credit_card') return false;
      if (parsedTypeFE === 'bank' && !['bank'].includes(aType)) return false;
      if (parsedTypeFE === 'demat' && !['demat', 'investment'].includes(aType)) return false;
      return true;
    });
    if (tier2) return tier2;
  }

  return null;
}

// Phase 54 hotfix v2.7.x, Error boundary around the entire StatementImport
// page so a throw anywhere (deep-link, preview render, dedup banner, etc.)
// never blanks the screen. Shows a friendly message + the actual error so
// the user can paste it back to me for surgical repair.
class _ImportErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null, info: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error('[ImportErrorBoundary]', err, info);
    this.setState({ info });
  }
  render() {
    if (!this.state.err) return this.props.children;
    const e = this.state.err;
    const msg = e?.message || String(e);
    const stack = (e?.stack || '').split('\n').slice(0, 6).join('\n');
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="import-error-boundary">
        <div className="max-w-xl rounded-2xl border border-border/70 bg-card p-5 space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[hsl(var(--gold))]" />
            <h2 className="text-sm font-medium">Something went wrong on this page.</h2>
          </div>
          <p className="text-xs text-muted-foreground">
            The import page hit an unexpected error. Your data is safe — no transactions
            were imported. Please share the message below with support so we can fix it.
          </p>
          <div className="rounded-xl bg-muted/40 p-3 text-[11px] font-mono whitespace-pre-wrap break-words">
            <div className="text-foreground">{msg}</div>
            {stack && <div className="mt-2 text-muted-foreground">{stack}</div>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => { window.location.href = '/import'; }} data-testid="import-error-reset">
              Reload import page
            </Button>
            <Button variant="ghost" className="rounded-xl" onClick={() => { window.location.href = '/dashboard'; }}>
              Back to dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

function StatementImportInner({ prefillFile = null, embedded = false } = {}) {
  usePageTitle('Statement Import');
  const { user, profile, session } = useAuth();
  const { flags } = useFeatureFlags();
  const isNewAuthorityUi = !!flags['imports.single_authority_ui'];
  const currency = profile?.currency || 'INR';
  const plan = effectivePlan(profile);
  const onTrial = isOnTrial(profile);
  const canParsePdf = hasFeature(plan, 'csv_import') && plan !== 'free';
  const [mode, setMode] = useState('single'); // 'single' | 'bulk'
  const [usage, setUsage] = useState(null); // { caps:{pdf_parse}, used:{pdf_parse}, ... }
  // Phase 44, Onboarding pool: served from /api/billing/onboarding-pool so
  // we can show the one-time welcome banner alongside the pool pill.
  const [poolDetail, setPoolDetail] = useState(null); // { pool, welcome_seen_at, plan }
  // v3.4.2 — persistent import state lives in SingleImportContext so
  // categorising rows, navigating to /transactions, and coming back
  // doesn't wipe the work. Fields lifted: stage, sourceType, fileName,
  // previewRows, accountId. Everything else stays local. See
  // /app/frontend/src/context/SingleImportContext.js.
  const {
    previewRows, setPreviewRows,
    sourceType, setSourceType,
    fileName, setFileName,
    accountId, setAccountId,
    stage, setStage,
    resetSingleImport,
    hasResumableImport,
  } = useSingleImport();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueue: enqueueJob, fetchJob, markConsumed } = useParseJobs({ noPoll: true });
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  // Phase 71C — user-chosen date format for CSV imports. Defaults to
  // 'auto' which runs detectDateFormat() across the entire date column
  // and picks DMY or MDY based on any unambiguous values (day > 12 or
  // month-slot > 12). User can override from the Map columns step.
  const [dateFormat, setDateFormat] = useState(DATE_FMT_AUTO);
  // previewRows now lives in SingleImportContext (see top of component).
  // H1 (Phase 81.5.4 companion hotfix) — deterministic file-currency signal.
  // Populated from the generic-CSV detector (see lib/csvCurrencyDetect.js) or
  // from job.result.account.currency for PDF flows. `null` means no reliable
  // signal — the preview falls back to the selected account's currency and
  // NO mismatch warning is shown (per user directive: never guess).
  const [detectedCurrency, setDetectedCurrency] = useState(null);
  // Phase 54 hotfix v2.7.x, dedup summary banner state. Populated by the
  // v2 PDF deep-link effect when job.result.dedup_summary exists. Used by
  // the preview banner ("X already in your ledger, skipped") and read in
  // confirm() to produce the success toast.
  const [dedupSummary, setDedupSummary] = useState(null);
  // v3.19.4 — post-commit summary shown on the 'done' stage.
  const [importResult, setImportResult] = useState(null);
  const [rules, setRules] = useState([]);
  const [learnDialog, setLearnDialog] = useState(null); // { index, pattern, category, applyAll }
  // Phase B (2026-05-21) — Installment plan detector: indices dismissed by the
  // user so the same suggestion doesn't re-appear on subsequent re-renders.
  const [dismissedInstallmentPairs, setDismissedInstallmentPairs] = useState(() => new Set());
  const [parseError, setParseError] = useState(null); // human-readable error for inline display
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });
  // v3.5.3 Phase 84.26 — per-row preflight errors returned by the backend
  // when a commit is blocked before it hits the RPC. Shape:
  //   Map<line_no, { reason: string, hint: string }>
  // Rendered as an inline red pill next to the offending row in the preview.
  const [rowErrors, setRowErrors] = useState(new Map());
  // Phase 85.1 — after a successful commit we surface the debt-payment
  // reconciliation summary for that import (suggestions the user can confirm).
  const [debtReconcileImportId, setDebtReconcileImportId] = useState(null);
  // v3.6.7 Phase 84.28.5 — user resolutions for preflight-flagged rows.
  //   Map<stage_row_id, 'keep' | 'drop'>
  //   'keep' → commit sends { stage_row_id, allow_dupe: true }
  //   'drop' → commit sends { stage_row_id, deselected: true }
  // Only meaningful when the row is currently in rowErrors — outside of
  // that it's ignored so a stale resolution can't drop a good row.
  const [rowResolutions, setRowResolutions] = useState(new Map());
  const fileRef = useRef(null);

  // ----- Phase 17: account selector + duplicate detection ------------------
  // The user must pick which account a statement belongs to BEFORE upload.
  // Loaded once on mount; refreshed if the user adds a new account inline.
  const [accounts, setAccounts] = useState([]);
  // accountId now lives in SingleImportContext (see top of component).
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  // Duplicate-warning dialog state. When a file's SHA-256 matches a prior job,
  // we open a calm dialog and let the user choose to proceed or cancel.
  const [dupDialog, setDupDialog] = useState(null); // { file, file_hash, file_size, match }

  // ----- Phase 41, Statement-aware account reconciliation ----------------
  // Shown after a PDF parse completes when the statement reveals a new
  // account (last4/bank_name) that isn't on file yet. Always asks the user
  // before creating/linking, no silent auto-create.
  const [statementAccountDialog, setStatementAccountDialog] = useState(null);
  // { account, txnCount, jobId }
  // Defaults to pre-fill the inline "New account" dialog when the user
  // accepts the suggestion from StatementAccountDialog.
  const [inlineAccountPrefill, setInlineAccountPrefill] = useState(null);

  // ----- Phase 42, Locked PDF (password-protected) ------------------------
  // Stays open across wrong-password retries so the user doesn't re-upload.
  const [lockedPdf, setLockedPdf] = useState(null);
  // { file, file_hash, file_size, errorMessage, busy }

  const loadAccounts = async () => {
    try {
      const { data, error } = await supabase
        .from('accounts')
        .select('id, name, type, last4, currency')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setAccounts(data || []);
      setAccountsLoaded(true);
      // If only one account exists, auto-select it for convenience.
      if ((data || []).length === 1 && !accountId) setAccountId(data[0].id);
    } catch (e) {
      console.warn('Could not load accounts for import:', e?.message || e);
      setAccountsLoaded(true);
    }
  };
  useEffect(() => { if (user?.id) loadAccounts(); /* eslint-disable-next-line */ }, [user?.id]);

  // ----- Load existing merchant rules so we can apply them on CSV preview ----
  // Phase 29.1, Refocus resilience: depend on stable `session?.user?.id`,
  // not the full session object (which Supabase replaces on every token
  // refresh, causing this effect to refire and clobber loaded rules).
  useEffect(() => {
    if (!session?.access_token) return;
    axios.get(`${BACKEND_URL}/api/merchant-rules`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    }).then((r) => setRules(r.data?.rules || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  const applyRules = (description) => {
    if (!description || !rules.length) return null;
    const d = description.toLowerCase();
    const sorted = [...rules].sort((a, b) => (b.pattern?.length || 0) - (a.pattern?.length || 0));
    for (const r of sorted) {
      const p = (r.pattern || '').toLowerCase().trim();
      if (p && d.includes(p)) return { category: r.category, source: 'learned' };
    }
    return null;
  };

  const reset = () => {
    setStage('upload'); setSourceType(null); setHeaders([]); setRows([]); setMapping({}); setDateFormat(DATE_FMT_AUTO); setPreviewRows([]); setFileName(''); setParseError(null); setDedupSummary(null); setDetectedCurrency(null);
    setImportResult(null);
    // v3.4.2 — also clear the SingleImportContext sessionStorage backup
    // so a fresh upload doesn't rehydrate the just-abandoned rows.
    resetSingleImport();
    if (fileRef.current) fileRef.current.value = '';
  };

  // v3.19.4 — what actually landed, for the "Import complete" card. Before
  // this, the modern staged path called reset() on success: the user was
  // dropped back on an empty upload screen with only a toast, and had no way
  // to see the rows they had just imported.
  const summariseImport = (rows, { count, skipped = 0, chunked = false }) => {
    const dates = (rows || []).map((r) => r.txn_date).filter(Boolean).sort();
    const acc = (accounts || []).find((a) => a.id === accountId) || null;
    // The autosaved draft is what powers the shell's "Resume import" pill.
    // Clearing it here (rather than in reset(), which the success path no
    // longer calls) stops a completed import from nagging the user to resume.
    resetSingleImport();
    setImportResult({
      count,
      skipped,
      chunked,
      accountId: accountId || null,
      accountName: acc?.name || null,
      accountLast4: acc?.last4 || null,
      from: dates[0] || null,
      to: dates[dates.length - 1] || null,
      fileName,
    });
  };

  // Load AI usage on mount so both single + bulk can pre-flight check the cap
  // Phase 29.1, Refocus resilience: depend on stable `session?.user?.id`.
  useEffect(() => {
    if (!session?.access_token) return;
    const h = { Authorization: `Bearer ${session.access_token}` };
    axios.get(`${BACKEND_URL}/api/usage/summary`, { headers: h })
      .then((r) => setUsage(r.data)).catch(() => {});
    // Phase 44, separate endpoint returns welcome_seen_at so we can decide
    // whether to show the one-time conversion banner.
    axios.get(`${BACKEND_URL}/api/billing/onboarding-pool`, { headers: h })
      .then((r) => setPoolDetail(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  const refreshUsage = () => {
    if (!session?.access_token) return;
    const h = { Authorization: `Bearer ${session.access_token}` };
    axios.get(`${BACKEND_URL}/api/usage/summary`, { headers: h })
      .then((r) => setUsage(r.data)).catch(() => {});
    axios.get(`${BACKEND_URL}/api/billing/onboarding-pool`, { headers: h })
      .then((r) => setPoolDetail(r.data)).catch(() => {});
  };

  const pdfCap = usage?.caps?.pdf_parse ?? null;
  const pdfUsed = usage?.used?.pdf_parse ?? 0;
  const pdfRemaining = pdfCap == null ? null : Math.max(0, pdfCap - pdfUsed);
  // Phase 44, Onboarding pool overrides the monthly cap when active.
  const pool = usage?.onboarding_pool || null;
  const poolActive = Boolean(pool?.active);
  const poolRemaining = pool?.remaining ?? 0;
  const effectiveRemaining = poolActive ? poolRemaining : pdfRemaining;
  const capExhausted = effectiveRemaining === 0;

  const handleFile = async (file) => {
    if (!file) return;
    const isPdf = /\.pdf$/i.test(file.name) || file.type === 'application/pdf';
    // Phase 84.1 — Additional deterministic formats. Both OFX (.ofx / .qfx)
    // and modern Excel (.xlsx) route through the SAME storage-upload +
    // /api/import/csv/stage path as CSV. The backend parses via
    // lib/importers/ (ofx_parser / xlsx_parser) which emit the same
    // ParseResult shape, so the downstream preview + commit are format-
    // agnostic. We force the New Authority path for these formats because
    // the legacy client-side Papa.parse fallback cannot handle OFX or
    // XLSX bytes.
    const isOfx  = /\.(ofx|qfx)$/i.test(file.name);
    const isXlsx = /\.xlsx$/i.test(file.name) ||
                    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    // Pre-flight cap check: if PDF and cap exhausted, block immediately
    if (isPdf && canParsePdf && capExhausted) {
      setFileName(file.name);
      setParseError({
        kind: 'cap',
        title: 'Your PDF parsing limit for this month has been reached',
        detail: `You’ve used all ${pdfCap} PDF parses on your ${onTrial ? 'Horizon trial' : 'current plan'} this month. It resets on the 1st, or upgrade for more headroom.`,
        status: 429,
      });
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    // Phase 41, Account selection is NO LONGER required pre-upload. If the
    // statement's account can be detected from the header, we'll ask the user
    // post-parse via StatementAccountDialog. If the user has already picked an
    // account from the picker, we respect it (no detection prompt fires for
    // confidently-matched accounts). This dramatically reduces friction:
    // user can drop a PDF immediately and 2120 figures out the rest.
    setFileName(file.name);
    if (isPdf) {
      if (!canParsePdf) {
        toast.error('PDF statement parsing is available on Clarity and above. CSV continues to work on Free.');
        return;
      }
      setSourceType('pdf');
      // Compute hash + check duplicate BEFORE enqueueing.
      const file_hash = await sha256OfFile(file);
      const file_size = file.size || null;
      if (file_hash) {
        const dup = await checkDuplicate({
          token: session?.access_token,
          file_hash,
          account_id: accountId,
          filename: file.name,
        });
        if (dup?.duplicate && dup.match) {
          // Open soft-warning dialog; user can proceed or cancel.
          setDupDialog({ file, file_hash, file_size, match: dup.match });
          if (fileRef.current) fileRef.current.value = '';
          return;
        }
      }
      handlePdf(file, { file_hash, file_size });
    } else if (isOfx || isXlsx) {
      // Phase 84.1 — OFX / XLSX always go through the server-authority
      // stage path. Account must be pre-selected because the backend
      // needs it to key the csv_imports parent row.
      setSourceType('csv');
      if (!user?.id) return;
      if (!accountId) {
        toast.error(`Please pick a linked account before uploading a ${isOfx ? 'OFX' : 'XLSX'} file.`, { duration: 5000 });
        if (fileRef.current) fileRef.current.value = '';
        return;
      }
      try {
        await handleCsvStaged(file);
      } catch (e) {
        console.warn(`[84.1] ${isOfx ? 'OFX' : 'XLSX'} staged path failed:`, e);
        toast.error(`Could not import: ${e?.message || 'unknown error'}`);
      }
    } else {
      setSourceType('csv');
      // -------------------------------------------------------------
      // Phase 81.5.4 F5 — CSV Single Authority upload path
      // -------------------------------------------------------------
      // Flag ON: upload bytes to csv-imports/<user_id>/<uuid>.csv via
      // the Supabase JS client (RLS-scoped), then call
      // /api/import/csv/stage which parses server-side and returns
      // engine-produced proposed envelopes. Client-side parsing +
      // autoCategorize() are non-authoritative.
      // Flag OFF: legacy client-parse via handleCsv().
      // -------------------------------------------------------------
      if (isNewAuthorityUi && user?.id) {
        if (!accountId) {
          toast.error('Please pick a linked account before uploading a CSV.', { duration: 5000 });
          if (fileRef.current) fileRef.current.value = '';
          return;
        }
        try {
          await handleCsvStaged(file);
          return;
        } catch (e) {
          console.warn('[F5] CSV staged path failed, falling back to legacy:', e);
          toast.error(`Server CSV upload failed: ${e?.message || 'unknown error'}. Falling back to legacy path.`);
          // Fall through to legacy path so the user isn't stranded.
        }
      }
      handleCsv(file);
    }
  };

  // ---------------------------------------------------------------------
  // Phase 84.2.1 — Auto-start when a File is prefilled by the Import Hub
  // ---------------------------------------------------------------------
  // The adaptive `/import` landing (ImportHub.jsx) can hand off the File
  // the user already dropped. We must trigger handleFile() exactly once,
  // when readiness conditions are met, without breaking the manual
  // upload path on `/import/statements`.
  //
  // Readiness rules (mirror what handleFile itself enforces):
  //   - PDF: fires as soon as accounts are loaded (account no longer
  //     required pre-upload — see Phase 41). `session?.access_token`
  //     must be present so the enqueue step can auth.
  //   - CSV / OFX / XLSX: waits until the user picks an `accountId`
  //     (backend keys csv_imports by account).
  // We latch on a ref so re-renders after handleFile() don't retrigger.
  const prefillFiredRef = useRef(false);
  useEffect(() => {
    if (!prefillFile) return;
    if (prefillFiredRef.current) return;
    if (stage !== 'upload') return;
    if (!accountsLoaded) return;
    if (!session?.access_token) return;
    const name = prefillFile.name || '';
    const isPdf = /\.pdf$/i.test(name) || prefillFile.type === 'application/pdf';
    // Non-PDF formats need an account pre-selected before upload.
    if (!isPdf && !accountId) return;
    prefillFiredRef.current = true;
    // Small microtask defer so any in-flight state settles first.
    Promise.resolve().then(() => {
      try {
        handleFile(prefillFile);
      } catch (e) {
        console.error('[84.2.1] prefill handleFile threw', e);
        toast.error('Could not start importing that file. Please try again.');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillFile, stage, accountsLoaded, accountId, session?.access_token]);

  // Proceed from the duplicate-warning dialog: enqueue anyway.
  const proceedDespiteDuplicate = () => {
    if (!dupDialog) return;
    const { file, file_hash, file_size } = dupDialog;
    setDupDialog(null);
    setFileName(file.name);
    setSourceType('pdf');
    // Phase 81.5.4 F5.remediation — "Import anyway" must never silently
    // no-op. Pass forceRetry=true so the server v2 endpoint treats a
    // completed-duplicate as an explicit override, and treats an active/
    // staged duplicate as a resume (not a block). Failed duplicates
    // ALWAYS create a fresh retry regardless of force flag.
    handlePdf(file, { file_hash, file_size, forceRetry: true });
  };

  // ---------------------------------------------------------------------
  // Phase 81.5.4 F5 — CSV Statement Import Single Authority upload
  // ---------------------------------------------------------------------
  // 1. Upload raw CSV bytes to Supabase Storage bucket 'csv-imports' at
  //    <auth.uid()>/<uuid>.csv. RLS policies (F4.1 §2) restrict this
  //    key space to the calling user only.
  // 2. Call POST /api/import/csv/stage with the storage_key. Server
  //    computes SHA-256, parses via the deterministic non-LLM CSV
  //    parser, runs the categorization engine, and durably records
  //    proposed envelopes into import_stage_rows. Response includes
  //    `duplicate` / `already_committed` flags per F4.1 §3.
  // 3. Convert `proposed` envelopes into preview rows with stage
  //    provenance (_import_id, _stage_etag, _line_no, ...) so confirm()
  //    routes through POST /api/import/commit.
  // ---------------------------------------------------------------------
  const handleCsvStaged = async (file) => {
    if (!user?.id) throw new Error('Not authenticated');
    if (!accountId) throw new Error('Please pick a linked account first.');

    setStage('parsing');
    setSourceType('csv');
    setFileName(file.name);
    setParseError(null);

    // 1) Upload to csv-imports/<uid>/<uuid>.<ext> via Supabase JS (RLS-scoped).
    // Phase 84.1 — preserve the file's real extension so operators tracing
    // Supabase Storage can identify OFX / XLSX uploads at a glance. The
    // backend still routes off `original_filename` (see imports_csv_stage.py)
    // so the storage-key extension is purely for observability.
    const _extMatch = /\.([a-zA-Z0-9]+)$/.exec(file.name || '');
    const _ext = _extMatch ? _extMatch[1].toLowerCase() : 'csv';
    const _knownExt = ['csv', 'tsv', 'ofx', 'qfx', 'xlsx'].includes(_ext) ? _ext : 'csv';
    const _contentType =
      _knownExt === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' :
      _knownExt === 'ofx' || _knownExt === 'qfx' ? 'application/x-ofx' :
      'text/csv';
    // BUG-005 — ask the server for a signed upload URL instead of writing
    // into a bucket directly. A direct upload needs a storage INSERT policy
    // to exist in that Supabase project, which is exactly what differed
    // between preview and production and broke CSV import there. A signed
    // URL needs no policy, and the server picks the `<uid>/` key.
    // See docs/STORAGE_BUCKETS.md.
    const t0 = session?.access_token || null;
    if (!t0) throw new Error('Not authenticated');
    const { data: signed } = await axios.post(
      `${BACKEND_URL}/api/import/csv/upload-url`,
      { filename: `upload.${_knownExt}` },
      { headers: { Authorization: `Bearer ${t0}` } },
    );
    const objectKey = signed.storage_key;
    const putRes = await fetch(signed.signed_url, {
      method: 'PUT',
      headers: { 'Content-Type': _contentType, Authorization: `Bearer ${signed.token}` },
      body: file,
    });
    if (!putRes.ok) {
      throw new Error(`storage_upload_failed: ${putRes.status} ${await putRes.text()}`);
    }

    // 2) Call /api/import/csv/stage.
    const t = session?.access_token || null;
    if (!t) throw new Error('Not authenticated');
    let stageRes;
    try {
      stageRes = await axios.post(
        `${BACKEND_URL}/api/import/csv/stage`,
        {
          account_id: accountId,
          storage_key: objectKey,
          original_filename: file.name,
        },
        { headers: { Authorization: `Bearer ${t}` } },
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = detail?.code || detail?.error;
      throw new Error(`csv_stage_failed: ${code || err?.message || 'unknown'}`);
    }

    const data = stageRes.data || {};
    // Duplicate + already-committed handling per F4.1 §3.
    if (data.already_committed) {
      setStage('upload');
      setParseError({
        kind: 'already-imported',
        title: 'This CSV was already imported',
        detail: `The exact same file bytes (SHA-256) were committed previously. ${data.commit_summary?.verified_ledger_rows || 0} transactions are already in your ledger.`,
      });
      return;
    }

    // 3) Build preview rows with stage provenance.
    const proposed = data.proposed || [];
    const previewed = proposed.map((s) => {
      const raw = s.raw || {};
      const env = s.proposed_envelope || {};
      const inferredType = env.txn_type || raw.type || 'expense';
      return {
        txn_date: raw.txn_date || todayISO(),
        description: raw.description || '',
        original_narration: raw.description || '',
        amount: Math.abs(Number(raw.amount) || 0),
        type: inferredType,
        category: env.category || (inferredType === 'transfer' ? 'Other Transfer' : 'Miscellaneous'),
        subcategory: env.subcategory || null,
        category_source: env.category_source || 'engine',
        needs_review: !!env.needs_review,
        _is_duplicate: false,
        _stage_row_id: s.stage_row_id,
        _line_no: s.line_no ?? raw.line_no,
        _import_id: data.csv_import_id,
        _import_kind: 'csv',
        _stage_etag: data.stage_etag,
        _authority_mode: 'commit_v1_stage',
        _proposed_envelope: env,
      };
    }).filter((r) => r.amount > 0);

    setPreviewRows(previewed);
    setDetectedCurrency(
      typeof data.detected_currency === 'string' && data.detected_currency.length === 3
        ? data.detected_currency.toUpperCase() : null,
    );
    setDedupSummary(null);
    setMode('single');
    setStage('preview');
    if (typeof console !== 'undefined') {
      console.info(`[F5] csv preview loaded via commit_v1_stage; rows=${previewed.length}, duplicate=${!!data.duplicate}`);
    }
    if (data.duplicate) {
      toast.info('This exact CSV was uploaded before — showing the previously staged preview.', { duration: 4500 });
    }
  };

  const handleCsv = (file) => {
    // Two-pass parse: first read raw rows (no header), find the true
    // header line, then re-parse with the right offset. This fixes
    // Fidelity / Vanguard / some HDFC exports that prepend several
    // preamble rows (account name, plan, balance) before the table.
    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (rawRes) => {
        const allRows = rawRes.data || [];

        // Phase 73 hotfix — multi-section support (e.g. Vanguard
        // OfxDownload). Scan the whole file for *every* candidate header
        // row, not just the first one. If a holdings-shaped section is
        // followed by a transaction-shaped section, prefer the txn
        // section so we don't wrongly reject a file that actually
        // contains importable data.
        const allHeaders = detectAllHeaderRows(allRows, { minKeywords: 3 });
        let detected = null;
        let holdingsHeader = null;   // for future Investment Import handoff

        if (allHeaders.length > 0) {
          // Prefer the first *transaction-shaped* header. Skip any
          // holdings-only sections that appear above it.
          const txnHeader = allHeaders.find((h) => !detectHoldingsSnapshot(h.headerRow));
          if (txnHeader) {
            detected = txnHeader;
            const first = allHeaders[0];
            if (first !== txnHeader && detectHoldingsSnapshot(first.headerRow)) {
              holdingsHeader = first;
              // eslint-disable-next-line no-console
              console.info(
                `[CSV import] Multi-section file: skipped holdings section at row ${first.headerRowIndex + 1}, `
                + `using transactions section at row ${txnHeader.headerRowIndex + 1}.`
              );
            }
          } else {
            // Nothing transaction-shaped anywhere → this really is a
            // holdings-only file. Fall through to the old rejection.
            detected = allHeaders[0];
          }
        }

        if (detected && detectHoldingsSnapshot(detected.headerRow)) {
          // This is a positions/holdings CSV — there are no transactions
          // to import. Surface a clear banner instead of importing the
          // header row as if it were data.
          setParseError({
            kind: 'holdings_snapshot',
            title: 'This looks like a holdings snapshot, not a transaction statement.',
            detail:
              'We see investment positions (Symbol / Quantity / Market Value) but no Date or Amount columns. '
              + "Try downloading 'Activity' or 'Transaction history' from your brokerage instead.",
          });
          setStage('upload');
          return;
        }

        let headers = [];
        let dataRows = [];

        if (detected) {
          // Re-build the row-of-objects shape that the rest of the page
          // expects, using the detected header row.
          headers = detected.headerRow.map((h, i) => {
            const v = h === null || h === undefined ? '' : String(h).trim();
            return v || `column_${i + 1}`;
          });
          const body = allRows.slice(detected.headerRowIndex + 1);
          dataRows = body
            .filter((r) => Array.isArray(r) && r.some((c) => String(c || '').trim() !== ''))
            .map((r) => {
              const obj = {};
              headers.forEach((h, i) => { obj[h] = r[i] !== undefined ? r[i] : ''; });
              return obj;
            });
          if (detected.headerRowIndex > 0) {
            // eslint-disable-next-line no-console
            console.info(`[CSV import] Skipped ${detected.headerRowIndex} preamble row(s); detected header on row ${detected.headerRowIndex + 1}.`);
          }
        } else {
          // No row crossed the keyword threshold → fall back to legacy
          // behaviour (treat row 0 as header). This preserves the import
          // path for unusual exports we haven't seen.
          if (allRows.length > 0 && Array.isArray(allRows[0])) {
            headers = allRows[0].map((h, i) => {
              const v = h === null || h === undefined ? '' : String(h).trim();
              return v || `column_${i + 1}`;
            });
            dataRows = allRows.slice(1)
              .filter((r) => Array.isArray(r) && r.some((c) => String(c || '').trim() !== ''))
              .map((r) => {
                const obj = {};
                headers.forEach((h, i) => { obj[h] = r[i] !== undefined ? r[i] : ''; });
                return obj;
              });
          }
        }

        setHeaders(headers);
        setRows(dataRows);
        // H1 · deterministic currency detection over parsed headers + rows.
        // Runs BEFORE the amount-cell strip in _parseAmount, so currency
        // symbols and ISO codes are still present. Returns null on
        // no-signal or conflicting-signal (never guesses).
        try {
          const detected = detectCurrencyFromCsv({ headers, rows: dataRows });
          setDetectedCurrency(detected || null);
        } catch (detErr) {
          // Detection must never break the import flow.
          // eslint-disable-next-line no-console
          console.warn('[csv currency detect] skipped:', detErr?.message || detErr);
          setDetectedCurrency(null);
        }
        const map = {};
        // Phase 65.1a — priority-aware mapping. Resolve `*_secondary`
        // guesses (Settlement Date, Principal Amount) only if no primary
        // guess exists for that role.
        const rawGuesses = headers.map((h) => guessMapping(h));
        const resolved   = resolveSecondaryGuesses(rawGuesses);
        headers.forEach((h, i) => { map[h] = resolved[i]; });
        setMapping(map);
        setStage('mapping');
      },
      error: (err) => toast.error('Could not parse CSV: ' + err.message),
    });
  };

  // Deep-link: /import?job=<id>, load that job's results into the preview screen
  useEffect(() => {
    const jobId = searchParams.get('job');
    if (!jobId || !session?.access_token) return;
    // Phase 41, wait for the accounts list to load before running the
    // deep-link effect; otherwise findMatchingAccount() sees an empty array
    // and we'd show the "New account detected" dialog even when a matching
    // account exists.
    if (!accountsLoaded) return;
    (async () => {
      try {
        const job = await fetchJob(jobId);
        if (!job) return;
        // Status vocabulary lives in lib/importStatus.js. The imported check
        // runs FIRST: the commit RPC's terminal status is `committed` (pdf) /
        // `completed` (csv), and treating those as "not a success status" made
        // the bell's row say "That import is committed — check the bell for
        // updates", which is where the user just came from.
        const state = jobState(job);
        if (state === 'imported' || state === 'purged' || job.consumed_at) {
          // Phase 17.2, refuse to re-open a job that's already been imported.
          // Otherwise the user can land on the preview screen and click "Import"
          // again, producing duplicate transactions. We surface a calm "already
          // imported" parseError card and skip rebuilding the preview.
          const count = importedCount(job) || job.result?.count
            || (job.result?.transactions || []).length || 0;
          setStage('upload');
          setPreviewRows([]);
          setFileName(job.filename || 'statement.pdf');
          setSourceType('pdf');
          setParseError({
            kind: 'already-imported',
            title: 'This statement is already imported',
            detail: `${count ? `${count} transactions from "${job.filename || 'this file'}"` : 'These transactions'} were added to your ledger ${relTimeShort(job.consumed_at || job.finished_at)}. You can find them on the Transactions page.`,
            consumed_at: job.consumed_at,
          });
          return;
        }
        if (state !== 'held') {
          toast.info(`That import is ${job.status} — check the bell for updates.`);
          return;
        }
        const txns = job.result?.transactions || [];
        // -----------------------------------------------------------------
        // Phase 81.5.4 F5 — Statement Import Single Authority preview path
        // -----------------------------------------------------------------
        // When this job was enqueued while the server flag was ON, the
        // worker wrote staged envelopes into import_stage_rows and the
        // backend now exposes them via `job.staged_envelopes` (see
        // routes/imports.py `GET /api/import/jobs/{id}`). We:
        //   1. Prefer staged envelopes over job.result.transactions.
        //   2. Carry stage provenance on each row so confirm() can route
        //      through POST /api/import/commit instead of the legacy
        //      direct-write path.
        //   3. Skip client autoCategorize()/rules — engine envelope is
        //      authoritative.
        // -----------------------------------------------------------------
        const isStagedPdf = job.authority_mode === 'commit_v1_stage'
          && job.stage_ready === true
          && Array.isArray(job.staged_envelopes)
          && job.staged_envelopes.length > 0;

        if (isStagedPdf) {
          const stagedPreviewed = job.staged_envelopes.map((s) => {
            const raw = s.raw || {};
            const env = s.proposed_envelope || {};
            const inferredType = env.txn_type || raw.type || 'expense';
            return {
              txn_date: raw.txn_date || todayISO(),
              description: raw.description || '',
              original_narration: raw.description || '',
              amount: Math.abs(Number(raw.amount) || 0),
              type: inferredType,
              category: env.category || (inferredType === 'transfer' ? 'Other Transfer' : 'Miscellaneous'),
              subcategory: env.subcategory || null,
              category_source: env.category_source || 'engine',
              needs_review: !!env.needs_review,
              _is_duplicate: false,
              _job_id: jobId,
              _stage_row_id: s.stage_row_id,
              _line_no: s.line_no ?? raw.line_no,
              _import_id: job.id,
              _import_kind: 'pdf',
              _stage_etag: job.stage_etag,
              _authority_mode: 'commit_v1_stage',
              _proposed_envelope: env,
            };
          }).filter((r) => r.amount > 0);
          setFileName(job.filename || 'imported_statement.pdf');
          setSourceType('pdf');
          setPreviewRows(stagedPreviewed);
          // v3.19.2 — restore the account the job is mapped to. Without this
          // the commit body carried no account_id, so an unmapped job could
          // only ever be refused (or, before the server guard, write ledger
          // rows attached to no account at all).
          if (job.account_id) setAccountId(job.account_id);
          // Currency seed from the parent job's detected_currency (server-side).
          const detCcy = job.detected_currency;
          setDetectedCurrency(
            typeof detCcy === 'string' && detCcy.length === 3
              ? detCcy.toUpperCase() : null,
          );
          setDedupSummary(null);   // dedup summary lives on commit_summary post-commit
          setMode('single');
          setStage('preview');
          if (typeof console !== 'undefined') {
            console.info('[F5] pdf preview loaded via commit_v1_stage; rows=', stagedPreviewed.length);
          }
          return;   // stop legacy preview construction
        }

        if (!txns.length) {
          setParseError({
            kind: 'empty',
            title: 'No transactions in this PDF',
            detail: job.result?.warning || 'The statement may be scanned or have an unfamiliar layout.',
          });
          return;
        }
        const previewed = txns.map((t) => {
          const desc = t.merchant || t.description || '';
          const narration = t.description || '';
          // Phase A, Defensive client-side ATM detection. Even when the LLM
          // misclassifies a cash withdrawal as 'expense', the heuristic below
          // recognises common ATM/CASH WDR patterns in the raw narration and
          // forces type='transfer' so the row stays out of expense totals.
          // The user can still override via the inline category editor.
          let inferredType = t.type || 'expense';
          if (isAtmWithdrawalDescription(narration) || isAtmWithdrawalDescription(desc)) {
            inferredType = 'transfer';
          }
          // Phase 29, Defensive client-side refund detection. Any incoming
          // money whose narration matches refund/reversal/cashback patterns is
          // coerced to type='refund' so it doesn't inflate the Income KPI or
          // the Clarity Score's income_visibility component. Note: refunds are
          // ALWAYS credits (money in), so the LLM typically picks 'income' for
          // them; this heuristic catches that mistake.
          if (inferredType === 'income' && (isRefundDescription(narration) || isRefundDescription(desc))) {
            inferredType = 'refund';
          }
          const ruleHit = applyRules(t.merchant || t.description);
          const baseCategory = ruleHit?.category
            || t.suggested_category
            || autoCategorize(t.merchant || t.description, inferredType)
            || (inferredType === 'transfer' ? 'Other Transfer' : inferredType === 'refund' ? 'Refunds & Reversals' : 'Miscellaneous');
          // For ATM/cash transfer rows, prefer the cash-flavoured category.
          // For refund rows, prefer the refund-flavoured category.
          const category = inferredType === 'transfer'
            ? autoCategorize(narration, 'transfer')
            : inferredType === 'refund'
              ? autoCategorize(narration || desc, 'refund')
              : baseCategory;
          return {
            txn_date: t.date || todayISO(),
            description: desc,
            original_narration: narration,
            amount: Number(t.amount) || 0,
            type: inferredType,
            category,
            category_source: ruleHit ? 'learned' : (t.category_source || 'ai'),
            needs_review: !!t.needs_review,
            // Worker-side fingerprint dedup (Phase 54 hotfix v2.7.x): when
            // this row already existed in the user's ledger from an earlier
            // parse, the backend marks it with `_duplicate: true`. We carry
            // it through so the preview can render a "duplicate" pill and
            // auto-deselect the row (worker already SKIPPED inserting it).
            _is_duplicate: !!t._duplicate,
            _job_id: jobId,
            // Phase 84.8 — carry the parser's source_line_no through so the
            // "apply category edits" UPDATE later can match by the unique
            // (source_job_id, source_line_no) tuple instead of a fragile
            // amount+description composite. Worker writes this on every
            // persisted row (see backend/worker.py::_persist_transactions).
            _line_no: t.line_no ?? null,
          };
        }).filter((r) => r.amount > 0);
        setFileName(job.filename || 'imported_statement.pdf');
        setSourceType('pdf');
        setPreviewRows(previewed);
        // H1 · seed detectedCurrency from the parser's detected statement
        // currency. Gemini emits ISO-3 on job.result.account.currency; we
        // treat null / missing / unsupported values as "no signal" so the
        // preview falls back to the selected account's currency and no
        // mismatch warning fires.
        {
          const parserCurrency = job.result?.account?.currency;
          setDetectedCurrency(
            typeof parserCurrency === 'string' && parserCurrency.length === 3
              ? parserCurrency.toUpperCase()
              : null,
          );
        }
        // Surface the parse result's dedup summary to the UI banner.
        setDedupSummary(job.result?.dedup_summary || null);
        setMode('single');
        setStage('preview');
        // Mark that the preview successfully rendered, any error thrown by
        // the post-preview reconciliation logic below MUST NOT bounce the
        // user back to the upload stage with a "Could not load" toast.
        var __previewRendered = true;

        // -------- Phase 41, account reconciliation ----------------------
        // After the preview is on screen, see whether the parser detected an
        // account identity. If a strong match exists in `accounts`, silently
        // preselect it and toast a calm confirmation. If no match but the
        // detector is confident enough, open the StatementAccountDialog.
        try {
          const parsedAccount = job.result?.account;
          if (parsedAccount && typeof parsedAccount === 'object') {
            const matched = findMatchingAccount(parsedAccount, accounts);
            if (matched) {
              // Silent link, just preselect and tell the user.
              setAccountId(matched.id);
              const last4 = matched.last4 ? ` ••${matched.last4}` : '';
              toast.success(`Linked to ${matched.name}${last4}.`, { duration: 4500 });
            } else if ((parsedAccount.detection_confidence || 0) >= 0.7) {
              // No match but the parser is confident, ask the user.
              setStatementAccountDialog({
                account: parsedAccount,
                txnCount: previewed.length,
                jobId,
              });
            }
            // detection_confidence < 0.7 → fall through silently; the user
            // can still pick an account from the picker on the preview screen.
          }
        } catch (recErr) {
          // Never let reconciliation break the import preview.
          console.warn('Account reconciliation skipped:', recErr?.message || recErr);
        }
        // -----------------------------------------------------------------

        // Clear the param so refresh doesn't re-trigger
        searchParams.delete('job');
        setSearchParams(searchParams, { replace: true });
      } catch (e) {
        // Phase 54 hotfix v2.7.x, only show the "Could not load" toast
        // when the preview itself failed to render. If the preview is
        // already on screen and a post-load step (e.g. account match)
        // threw, log it but DON'T bounce the user with a confusing error.
        console.error('[import deep-link] fetchJob/preview failed:', e);
        // eslint-disable-next-line no-undef
        if (typeof __previewRendered === 'undefined' || !__previewRendered) {
          toast.error(`Could not load that import: ${e?.message || 'unknown error'}`);
        }
      }
    })();
    // Phase 29.1, Refocus resilience: depend on `session?.user?.id` (stable)
    // not the full session object (which Supabase replaces on every token
    // refresh, causing this effect to refire mid-deep-link load).
    // Phase 41, also depend on `accountsLoaded` so reconciliation runs as
    // soon as the accounts list is ready (we early-return above if it isn't).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, session?.user?.id, accountsLoaded]);

  const handlePdf = async (file, opts = {}) => {
    // ----- Async path: enqueue a parse job, show a "submitted" state, ------
    // and let the global NotificationBell drive completion. The user can
    // navigate freely; when ready, they tap "Review & import" in the bell.
    //
    // Phase 42, If the PDF is password-protected, intercept BEFORE enqueue:
    // open the locked-pdf dialog and wait for the user to enter a password.
    // We detect encryption client-side (cheap byte scan) so we never even
    // send a doomed upload to the server. As a defensive fallback, if the
    // server returns 401 pdf_password_required, we open the dialog then too.
    if (!opts.pdf_password) {
      try {
        if (await isPdfEncrypted(file)) {
          setLockedPdf({
            file,
            file_hash: opts.file_hash || null,
            file_size: opts.file_size != null ? opts.file_size : (file.size || null),
            errorMessage: null,
            busy: false,
          });
          return;
        }
      } catch {
        // Detection failed → continue; the backend will tell us if encrypted.
      }
    }

    setStage('submitted');
    setParseError(null);
    try {
      const res = await enqueueJob(file, {
        account_id: accountId,
        file_hash: opts.file_hash || null,
        file_size: opts.file_size != null ? opts.file_size : (file.size || null),
        pdf_password: opts.pdf_password || null,
        forceRetry: !!opts.forceRetry,
      });
      toast.success('Statement queued. We’ll notify you in the bell when it’s ready.', { duration: 6000 });
      // Phase 42, close the locked dialog on success.
      setLockedPdf(null);
      refreshUsage();
      return res; // { job_id, status }
    } catch (e) {
      console.error('PDF enqueue error', e);
      const status = e.response?.status;
      const serverDetail = e.response?.data?.detail;
      // ---- Phase 42, backend signals password required/invalid ---------
      const errCode = serverDetail && typeof serverDetail === 'object' ? serverDetail.error : null;
      const errMsg = serverDetail && typeof serverDetail === 'object' ? serverDetail.message : null;
      if (status === 401 && (errCode === 'pdf_password_required' || errCode === 'pdf_password_invalid')) {
        setLockedPdf({
          file,
          file_hash: opts.file_hash || null,
          file_size: opts.file_size != null ? opts.file_size : (file.size || null),
          errorMessage: errCode === 'pdf_password_invalid' ? (errMsg || 'That password didn’t work. Try again.') : null,
          busy: false,
        });
        // Don't burn the parse credit on a password failure.
        setStage('upload');
        return;
      }
      // -------------------------------------------------------------------
      const isNetwork = !e.response && (e.code === 'ERR_NETWORK' || e.message === 'Network Error' || e.code === 'ECONNABORTED' || /timeout/i.test(e.message || ''));
      let title = 'We couldn’t accept this PDF';
      let detail = (typeof serverDetail === 'string' ? serverDetail : null) || e.message || 'Unknown error.';
      let kind = 'error';
      if (status === 429) {
        title = 'Your PDF parsing limit for this month has been reached';
        detail = (typeof serverDetail === 'string' ? serverDetail : null) || `Trial users get ${pdfCap || 4} PDF parses per month. It resets on the 1st, or upgrade for more headroom.`;
        kind = 'cap';
      } else if (status === 503) {
        title = 'AI service is temporarily unavailable';
        detail = (typeof serverDetail === 'string' ? serverDetail : null) || 'Please try again in a moment.';
      } else if (status === 413) {
        title = 'PDF is too large';
        detail = (typeof serverDetail === 'string' ? serverDetail : null) || 'Try a shorter date range or split the file.';
      } else if (isNetwork) {
        title = 'Your statement didn’t reach us';
        detail = 'The upload was interrupted before we could queue your statement. Please check your connection and try again, nothing has been processed or counted against your monthly limit.';
        kind = 'network';
      }
      setParseError({ kind, title, detail, status });
      setStage('upload');
      refreshUsage();
    }
  };

  // Phase 42, user submitted a password for the locked PDF; retry enqueue.
  const submitLockedPdfPassword = async (password) => {
    if (!lockedPdf) return;
    setLockedPdf((prev) => prev ? { ...prev, busy: true, errorMessage: null } : prev);
    await handlePdf(lockedPdf.file, {
      file_hash: lockedPdf.file_hash,
      file_size: lockedPdf.file_size,
      pdf_password: password,
    });
    // If still open, that means the attempt failed, re-enable retry.
    setLockedPdf((prev) => prev ? { ...prev, busy: false } : prev);
  };

  // ----- (old synchronous parse path retired in favor of async enqueue) -----

  // Phase 71C — derive the effective date format used by csvPreview.
  // Either honour the user's manual choice or auto-detect by scanning
  // the full date column for an unambiguous value.
  const detectedDateFormat = useMemo(() => {
    if (sourceType !== 'csv') return 'ambiguous';
    const dateHeader = Object.keys(mapping).find((h) => mapping[h] === 'date');
    if (!dateHeader) return 'ambiguous';
    return detectDateFormat(rows.map((r) => r[dateHeader]));
  }, [sourceType, mapping, rows]);

  const effectiveDateFormat = useMemo(() => {
    if (dateFormat !== DATE_FMT_AUTO) return dateFormat;
    if (detectedDateFormat === 'ambiguous') return DATE_FMT_DMY;
    return detectedDateFormat;
  }, [dateFormat, detectedDateFormat]);

  // CSV preview rows derived from rows+mapping
  const csvPreview = useMemo(() => {
    if (sourceType !== 'csv') return [];
    return rows.map((r) => {
      const obj = { type: 'expense', amount: 0, description: '', category: null, txn_date: todayISO(), category_source: 'rule' };
      Object.entries(mapping).forEach(([h, target]) => {
        if (target === '__skip') return;
        const val = r[h];
        if (target === 'date') obj.txn_date = parseDateLoose(val, effectiveDateFormat);
        else if (target === 'description') obj.description = (obj.description ? obj.description + ' ' : '') + (val || '');
        else if (target === 'amount') {
          const n = parseAmount(val);
          obj.amount = Math.abs(n);
          if (n < 0) obj.type = 'expense';
        }
        else if (target === 'debit') {
          const n = parseAmount(val);
          if (n > 0) { obj.amount = n; obj.type = 'expense'; }
        }
        else if (target === 'credit') {
          const n = parseAmount(val);
          if (n > 0) { obj.amount = n; obj.type = 'income'; }
        }
        else if (target === 'category') obj.category = (val || '').trim() || null;
      });
      obj.description = (obj.description || '').trim();
      // Phase A, Defensive ATM detection for CSV imports as well.
      if (isAtmWithdrawalDescription(obj.description)) {
        obj.type = 'transfer';
      }
      // Phase 29, Defensive refund detection. Coerce incoming-money rows
      // whose narration looks like a refund/cashback/reversal into
      // type='refund' so they don't inflate Income totals.
      if (obj.type === 'income' && isRefundDescription(obj.description)) {
        obj.type = 'refund';
      }
      if (!obj.category) {
        const learned = applyRules(obj.description);
        if (learned) { obj.category = learned.category; obj.category_source = 'learned'; }
        else { obj.category = autoCategorize(obj.description, obj.type); obj.category_source = 'rule'; }
      }
      return obj;
    }).filter((r) => r.amount > 0);
    // eslint-disable-next-line
  }, [rows, mapping, rules, sourceType]);

  // The active preview source (PDF stream or CSV-derived)
  // activePreview normally selects between the PDF path (previewRows) and
  // the legacy CSV path (csvPreview). Phase 81.5.4 F5: the new-authority
  // CSV path (handleCsvStaged) also populates previewRows with staged
  // envelopes, so prefer previewRows whenever any row carries the F5
  // stage-provenance marker (_authority_mode='commit_v1_stage'). This
  // keeps the legacy CSV render path intact for flag-OFF users.
  const activePreview = (() => {
    if (sourceType === 'pdf') return previewRows;
    const hasF5StagedCsv = Array.isArray(previewRows)
      && previewRows.length > 0
      && previewRows.some((r) => r?._authority_mode === 'commit_v1_stage' && r?._import_kind === 'csv');
    return hasF5StagedCsv ? previewRows : csvPreview;
  })();

  // Phase B (2026-05-21) — Installment plan detector.
  // Runs on the current preview rows so it works uniformly across PDF/CSV
  // flows. Detection is region-neutral (handles India/GCC EMI, Amex Plan It,
  // Citi Flex Plan, Chase Pay Over Time, Affirm, Klarna, Clearpay, Afterpay,
  // plus generic "installment/instalment" text). See lib/installmentDetector.
  const installmentClusters = useMemo(() => {
    try {
      return detectInstallmentConversions(activePreview || []);
    } catch (err) {
      // Detector must never crash the preview — degrade gracefully.
      // eslint-disable-next-line no-console
      console.warn('installment detector failed', err);
      return [];
    }
  }, [activePreview]);

  // Stable key per detected pair so dismissal survives re-renders even if
  // row order changes slightly. We key on the (purchase idx, reversal idx)
  // tuple which is stable within the same parsed batch.
  const clusterKey = React.useCallback(
    (cluster) => `${cluster?.purchase?.__idx ?? '?'}::${cluster?.reversal?.__idx ?? '?'}`,
    [],
  );

  const visibleInstallmentClusters = installmentClusters.filter(
    (c) => !dismissedInstallmentPairs.has(clusterKey(c)),
  );

  const applyInstallmentPreset = (cluster) => {
    try {
      const patches = computeClusterPreset(cluster);
      if (!patches || patches.size === 0) {
        toast.info('Nothing to apply — no plan schedule rows in this batch.');
        return;
      }
      const next = (previewRows || []).map((row, idx) => {
        const patch = patches.get(idx);
        if (!patch) return row;
        return { ...row, ...patch };
      });
      setPreviewRows(next);
      // Hide this specific card so it doesn't re-appear.
      setDismissedInstallmentPairs((prev) => {
        const nx = new Set(prev);
        nx.add(clusterKey(cluster));
        return nx;
      });
      toast.success(
        `Applied installment preset (${patches.size} row${patches.size > 1 ? 's' : ''} updated).`,
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('installment preset apply failed', err);
      toast.error('Could not apply the installment preset. Please try manually.');
    }
  };

  const dismissInstallmentCluster = (cluster) => {
    setDismissedInstallmentPairs((prev) => {
      const nx = new Set(prev);
      nx.add(clusterKey(cluster));
      return nx;
    });
  };

  // H1 · currency contract for the preview render.
  //   detectedCurrency : parser/file evidence only (may be null).
  //   accountCurrency  : selected account's currency (may be null when the
  //                      user hasn't picked one yet).
  //   previewCurrency  : detectedCurrency || accountCurrency || profileCurrency.
  //   currencyMismatch : true iff both are known AND unequal.
  // Recomputes naturally on accountId / accounts / detectedCurrency changes
  // via React state — no preview-row mutation, no reparse.
  const selectedAccount = useMemo(
    () => (accounts || []).find((a) => a.id === accountId) || null,
    [accounts, accountId],
  );
  const accountCurrency = selectedAccount?.currency
    ? String(selectedAccount.currency).toUpperCase()
    : null;
  const previewCurrency = detectedCurrency || accountCurrency || currency;
  const currencyMismatch = Boolean(
    detectedCurrency && accountCurrency && detectedCurrency !== accountCurrency,
  );

  const editCategory = (i, newCategory) => {
    if (sourceType === 'pdf') {
      const next = [...previewRows];
      next[i] = { ...next[i], category: newCategory, category_source: 'edited' };
      setPreviewRows(next);
      // Offer to remember the rule
      const pattern = suggestPattern(next[i].description);
      // Phase 84.2.3 — Learn dialog must use the row's transaction TYPE
      // (income / investment / transfer / expense / refund) so the
      // category picker inside the dialog shows the right taxonomy.
      // Previously it hard-coded 'expense' — picking Mutual Fund on an
      // investment row silently reset to a random expense category.
      if (pattern) setLearnDialog({ index: i, pattern, category: newCategory, description: next[i].description, type: next[i].type || 'expense', applyAll: true });
    } else {
      // For CSV path, we mutate previewRows via a parallel override (since csvPreview is derived).
      // Simpler: convert to a mutable array on first edit.
      const next = [...csvPreview];
      next[i] = { ...next[i], category: newCategory, category_source: 'edited' };
      setPreviewRows(next);
      setSourceType('pdf'); // switch to "stored" mode so further edits persist
      // Note: this is a simplification, once user starts editing, we treat the rows as fixed.
      const pattern = suggestPattern(next[i].description);
      if (pattern) setLearnDialog({ index: i, pattern, category: newCategory, description: next[i].description, type: next[i].type || 'expense', applyAll: true });
    }
  };

  const saveLearnDialog = async (edited = null) => {
    // v3.4.2 — accept edited values from <LearnRuleDialog> (local state) so
    // keystrokes in the dialog don't re-render the massive parent tree.
    // Fall back to the trigger's initial values if the dialog didn't
    // pass anything (defensive).
    if (!learnDialog) return;
    const pattern = edited?.pattern ?? learnDialog.pattern;
    const category = edited?.category ?? learnDialog.category;
    const applyAll = edited?.applyAll ?? learnDialog.applyAll;
    try {
      await axios.post(`${BACKEND_URL}/api/merchant-rules`, { pattern, category }, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      // Apply to all matching rows in current preview
      if (applyAll) {
        // Hotfix (2026-05-21): normalize BOTH sides — pattern and each
        // row's description — to a single-spaced, lowercased form before
        // substring compare. This makes propagation work reliably against
        // fixed-width bank statement rows (e.g.
        // "NOQODI GV KIOSK         DUBAI") regardless of whether the rule
        // was stored with padded or normalized whitespace.
        const normForMatch = (s) => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
        const needle = normForMatch(pattern);
        const next = (previewRows.length ? previewRows : csvPreview).map((r) => {
          if (needle && normForMatch(r.description).includes(needle)) {
            return { ...r, category, category_source: 'learned' };
          }
          return r;
        });
        setPreviewRows(next);
      }
      // refresh rules
      const r = await axios.get(`${BACKEND_URL}/api/merchant-rules`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      setRules(r.data?.rules || []);
      toast.success(`Learned: "${pattern}" → ${category}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save rule.');
    } finally {
      setLearnDialog(null);
    }
  };

  const confirm = async () => {
    if (!user) {
      toast.error('Please sign in again.');
      return;
    }
    if (!activePreview.length) { toast.error('Nothing to import.'); return; }
    if (importing) return;

    // ==================================================================
    // Phase 81.5.4 F5 — Statement Import Single Authority commit path
    // ==================================================================
    // When ANY row in the active preview carries stage provenance
    // (_import_id + _authority_mode='commit_v1_stage'), route ALL rows
    // through POST /api/import/commit. This is the ONLY writer to
    // public.transactions on the new path — no FE direct-write.
    //
    // The commit endpoint takes ONLY diff-only corrections keyed by
    // stage_row_id (contract: routes/imports_commit.py CommitRequest).
    // The server rebuilds writable_rows from the immutable stage rows
    // + these corrections. deselections use `deselected:true`.
    //
    // Same-key retries are safe: the RPC deterministically replays when
    // idempotency_key matches an existing completed row.
    // ==================================================================
    const stagedProbe = activePreview.find((r) => r._authority_mode === 'commit_v1_stage' && r._import_id);
    if (stagedProbe) {
      setImporting(true);
      setImportProgress({ done: 0, total: activePreview.length });
      // v3.5.3 Phase 84.26 — hoist bigToastId out of the try{} so the
      // catch{} can dismiss it too. Declared here so it's in scope for
      // both success and error paths.
      let bigToastId = null;
      try {
        const importId   = stagedProbe._import_id;
        const importKind = stagedProbe._import_kind || 'pdf';
        const stageEtag  = stagedProbe._stage_etag;

        // Build diff-only corrections. Compare each preview row to its
        // proposed envelope; emit a correction only when the user changed
        // category, type, or deselected the row.
        // v3.6.7 Phase 84.28.5 — user resolutions for preflight-flagged
        // rows override the default flow: 'drop' → deselected, 'keep' →
        // allow_dupe (server appends a marker so the fingerprint differs).
        const corrections = [];
        for (const r of activePreview) {
          if (!r._stage_row_id) continue;
          const env = r._proposed_envelope || {};
          const engineCat  = env.category ?? null;
          const engineType = env.txn_type ?? null;
          const resolution = rowResolutions.get(r._stage_row_id) || null;
          const isDeselected = !!(r._deselected || r._is_duplicate) || resolution === 'drop';
          if (isDeselected) {
            corrections.push({ stage_row_id: r._stage_row_id, deselected: true });
            continue;
          }
          const catChanged  = (r.category ?? null) !== engineCat;
          const typeChanged = (r.type ?? null)     !== engineType;
          const c = { stage_row_id: r._stage_row_id };
          if (catChanged)  c.category = r.category;
          if (typeChanged) c.type     = r.type;
          if (resolution === 'keep') c.allow_dupe = true;
          if (catChanged || typeChanged || resolution === 'keep') {
            corrections.push(c);
          }
        }

        // Deterministic idempotency key per (import, stage_etag).
        const idempotencyKey = `commit-${importId}-${stageEtag || 'noetag'}`;
        const t = session?.access_token
          || (await supabase.auth.getSession())?.data?.session?.access_token
          || null;
        if (!t) throw new Error('Not authenticated');
        // v3.5.3 Phase 84.26 — clear any prior preflight errors so a
        // successful subsequent commit doesn't keep the red pills lit up.
        setRowErrors(new Map());
        // Chunked-progress toast for large imports: show a persistent toast
        // ID so the success handler can update it in place.
        const bigImport = (activePreview?.length || 0) > 400;
        bigToastId = bigImport
          ? toast.loading(`Importing ${activePreview.length} rows in batches — this may take up to 90 seconds…`, { duration: 120000 })
          : null;
        const res = await axios.post(
          `${BACKEND_URL}/api/import/commit`,
          {
            import_kind: importKind,
            import_id: importId,
            idempotency_key: idempotencyKey,
            stage_etag: stageEtag,
            corrections,
            // v3.19.2 — the account this statement belongs to. The server
            // only uses it when the parse job was enqueued before an account
            // was picked; without it the commit used to write ledger rows
            // with account_id = NULL (imported, but attached to nothing).
            ...(accountId ? { account_id: accountId } : {}),
          },
          {
            headers: { Authorization: `Bearer ${t}` },
            // v3.5.3 Phase 84.26 — large statements (400+ rows) can spend
            // 30–60s inside the RPC. Axios's default (0 = no timeout) is
            // fine on the client, but some ingress layers cap at 30s.
            // Raising to 120s here lets slow imports survive the round-trip.
            timeout: 120000,
          },
        );
        setImportProgress({ done: activePreview.length, total: activePreview.length });
        const summary = res.data?.commit_summary;
        const chunked = summary?.chunked === true;
        const rowsInserted = summary?.verified_ledger_rows ?? (activePreview.length - corrections.filter((c) => c.deselected).length);
        const skippedCount = corrections.filter((c) => c.deselected).length;
        // Dismiss the "Importing large statement…" spinner toast (if any)
        // and replace with the final success message.
        if (bigToastId) toast.dismiss(bigToastId);
        toast.success(
          `Imported ${rowsInserted} transactions${skippedCount ? `, skipped ${skippedCount}` : ''}${chunked ? ' (batched)' : ''}.`,
          { duration: 5000 },
        );
        try {
          if (session) await triggerRecurringAutoConfirm(session);
        } catch { /* non-fatal */ }
        // v3.19.4 — land on a real confirmation instead of reset()-ing the
        // user back to an empty upload screen with nothing but a toast.
        summariseImport(activePreview, { count: rowsInserted, skipped: skippedCount, chunked });
        setStage('done');
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const errCode = (typeof detail === 'object' ? detail?.error : null) || err?.message;
        // Dismiss the "large import in flight" spinner toast so the error
        // isn't stacked on top of it.
        if (bigToastId) toast.dismiss(bigToastId);
        // v3.5.3 Phase 84.26 — inline "Retry" toast action for transient
        // failures. Sonner's `action` prop renders a small button next to
        // the message; on click we re-fire the SAME confirm() call. The
        // idempotency_key is derived from (importId, stage_etag), so a
        // successful out-of-band commit will replay cleanly.
        const retryAction = {
          label: 'Retry',
          onClick: () => { confirm(); },
        };
        if (status === 422 && errCode === 'commit_preflight_failed') {
          // Per-row preflight failures — mark the bad rows in the preview
          // so users can locate and fix them without guessing.
          // v3.6.5 fix — key by stage_row_id (stable) when the backend
          // provides it, falling back to line_no. Previously we only
          // keyed by line_no and the frontend's origIdx doesn't align
          // with the stage table's 1-indexed line_no once the preview
          // has filtered / re-ordered rows — so the red pill lookup
          // silently missed and "2 rows need fixing" showed no pills.
          const bads = Array.isArray(detail?.bad_rows) ? detail.bad_rows : [];
          const nextErrs = new Map();
          for (const b of bads) {
            const meta = { reason: b.reason, hint: b.hint, stage_row_id: b.stage_row_id, line_no: b.line_no };
            if (b?.stage_row_id) nextErrs.set(`sr:${b.stage_row_id}`, meta);
            if (b?.line_no != null) nextErrs.set(b.line_no, meta);
          }
          setRowErrors(nextErrs);
          // v3.6.6 Phase 84.28.4 — scroll the first offending row into
          // view so users don't have to hunt for the red pill in a long
          // preview. We wait one tick for React to re-render with the
          // new `.bg-destructive/5` class on the row, then scroll it
          // into the viewport with a bit of vertical padding.
          if (bads.length > 0) {
            const firstBad = bads[0];
            setTimeout(() => {
              try {
                const rows = document.querySelectorAll('[data-testid^="preview-row-error-"]');
                const target = rows[0];
                if (target && target.scrollIntoView) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              } catch { /* scroll is best-effort */ }
              // reference firstBad so ESLint doesn't flag as unused
              void firstBad;
            }, 120);
          }
          toast.error(
            `${bads.length} row(s) need fixing before this import can go through. See red pills in the preview below.`,
            { duration: 8000 },
          );
        } else if (status === 422 && (errCode === 'account_not_mapped' || errCode === 'account_not_found')) {
          toast.error(
            'Choose the account this statement belongs to, then import again — nothing was written.',
            { duration: 9000 },
          );
        } else if (status === 412 && errCode === 'stage_stale') {
          toast.error('Preview is stale — please reopen the import to see the latest categorization.');
        } else if (status === 409 && errCode === 'currency_mismatch') {
          toast.error(`Currency mismatch: statement is ${detail?.statement_currency}, account is ${detail?.account_currency}. Change the linked account and retry.`);
        } else if (status === 409 && errCode === 'already_committed') {
          toast.info('This import was already committed. Nothing to do.');
          reset();
        } else if (status === 409 && errCode === 'wrong_parent_status') {
          const parentStatus = detail?.detail || 'unknown';
          toast.error(
            `This statement isn't ready to import (status: ${parentStatus}). Please re-upload the file. If the issue persists, contact support@2120.money.`,
            { duration: 6000 },
          );
        } else if (status === 504 && errCode === 'commit_upstream_timeout') {
          toast.error(
            'The import took longer than expected. Nothing was committed — click Retry to try again.',
            { duration: 10000, action: retryAction },
          );
        } else if (status === 500 && errCode === 'commit_internal_error') {
          const sqlstate = detail?.sqlstate;
          const sqlmsg   = detail?.detail;
          toast.error(
            `Import didn't finish (server error${sqlstate ? ` · ${sqlstate}` : ''}). ` +
            `Nothing was written. Click Retry, or forward to support@2120.money: ${sqlmsg || 'no detail'}`,
            { duration: 12000, action: retryAction },
          );
        } else {
          toast.error(`Import failed: ${errCode || 'unknown error'}`, { action: retryAction });
        }
      } finally {
        setImporting(false);
      }
      return;
    }
    // ==================================================================
    // Legacy paths continue below — flag OFF or job predates F5.
    // ==================================================================

    // When the preview rows come from a v2 parse_job (any row carries
    // _job_id), the worker has ALREADY persisted them to the transactions
    // table *with* fingerprint-based dedup. Re-inserting from the FE here
    // would duplicate every row a second time. So for PDF flow we only:
    //   1. Apply any per-row category edits the user made in the preview
    //      (via UPDATE WHERE source_job_id+description match).
    //   2. Mark the job consumed so the bell stops nagging.
    //   3. Trigger account-balance sync if a closing balance is present.
    // CSV flow (rows have no _job_id) still goes through the bulk insert
    // path below unchanged.
    // ------------------------------------------------------------------
    const jobId = activePreview.find((r) => r._job_id)?._job_id;
    if (jobId) {
      setImporting(true);
      setImportProgress({ done: 0, total: activePreview.length });
      try {
        // Step 1, apply any user category edits via UPDATE. Match by
        // (source_job_id, source_line_no) — a UNIQUE index the worker
        // guarantees per persisted row — with a defensive fallback to
        // (source_job_id, description, txn_date, amount) for rows that
        // didn't carry line_no through the preview.
        //
        // Phase 84.8 hotfix — the previous fallback assumed on-disk
        // expense rows were stored as NEGATIVE amounts (the historical
        // imports_commit.py contract). The worker path, however, stores
        // `t.amount` verbatim from the parser — which is POSITIVE for
        // essentially every Indian bank PDF (Gemini emits |amt| into
        // separate debit/credit columns). The old .eq('amount', -abs(...))
        // therefore matched zero rows, tripping the "we could not save
        // your category edits" toast even though the transactions had
        // been imported successfully. We now:
        //   • prefer the (source_job_id, source_line_no) lookup, and
        //   • fall back to matching EITHER sign of the amount via .in_,
        //     so the update is sign-agnostic.
        //
        // F1/P0-D: This block previously used a fire-and-forget UPDATE that
        // reported success even when zero rows matched. That fails silently
        // for the exact case worker P0-A/B guards against — if the worker
        // fell into its silent stripped-provenance fallback, the ledger has
        // rows *without* source_job_id, so this UPDATE matches nothing and
        // the user is told "edits saved" while none actually were. We now:
        //   • request `.select('id')` so PostgREST returns matched rows,
        //   • count actual DB updates,
        //   • if the user had edits AND zero rows updated total, surface
        //     the Support.needs_reimport state instead of a success toast.
        const editedRows = activePreview.filter((r) => !r._is_duplicate);
        let editsApplied = 0;
        let editsAttempted = 0;
        let totalRowsMatched = 0;
        for (const r of editedRows) {
          // Only attempt update if the row actually has a category
          // (most do, since autoCategorize runs on every preview row).
          if (!r.category) continue;
          editsAttempted += 1;
          try {
            const a = Math.abs(Number(r.amount));
            let q = supabase
              .from('transactions')
              .update({ category: r.category, type: r.type })
              .eq('source_job_id', jobId);
            if (Number.isFinite(r._line_no) && r._line_no != null) {
              // Preferred: unique-index-backed match.
              q = q.eq('source_line_no', r._line_no);
            } else {
              // Fallback: composite match — amount can be positive OR
              // negative on disk depending on which persist path ran.
              q = q
                .eq('description', r.description)
                .eq('txn_date', r.txn_date)
                .in('amount', [a, -a]);
            }
            const { data, error } = await q.select('id');
            if (!error) {
              editsApplied += 1;
              totalRowsMatched += (data?.length ?? 0);
            }
          } catch (e) {
            // Best-effort; don't block import on edit failures.
          }
          setImportProgress({ done: editsApplied, total: editedRows.length });
        }

        // F1/P0-D: detect the "silent worker fallback" failure mode.
        // Symptom: user had rows to edit (editsAttempted > 0), every UPDATE
        // ran without an error (editsApplied === editsAttempted), but not
        // one row actually matched in the DB (totalRowsMatched === 0). This
        // means the worker persisted the rows without source_job_id (the
        // pre-F1 stripped-provenance fallback path). Rather than tell the
        // user "success" while their edits went nowhere, surface a clear
        // "needs re-import" state so they can act.
        if (editsAttempted > 0 && totalRowsMatched === 0) {
          console.error(
            'F1/P0-D: zero-row UPDATE detected for job',
            jobId,
            '- worker likely persisted without provenance; needs re-import'
          );
          toast.error(
            'We could not save your category edits. This statement needs to be re-imported. ' +
            'Please delete this job and try again — if the problem repeats, contact support.',
            { duration: 10000 }
          );
          try { await markConsumed(jobId); } catch { /* ignore */ }
          setStage('needs_reimport');
          return;
        }

        const inserted = dedupSummary?.inserted ?? activePreview.filter((r) => !r._is_duplicate).length;
        const dupes = dedupSummary?.duplicates_skipped ?? 0;
        if (dupes > 0) {
          toast.success(`Imported ${inserted} new transactions · skipped ${dupes} already in your ledger.`);
        } else {
          toast.success(`Imported ${inserted} transactions.`);
        }
        summariseImport(activePreview, { count: inserted, skipped: dupes });
        setStage('done');
        // Phase 71.2 — auto-confirm any recurring rules with fresh
        // evidence from this import. Idempotent + non-blocking.
        triggerRecurringAutoConfirm(session);

        // Mark job consumed so the bell stops showing "Review & import".
        try { await markConsumed(jobId); } catch { /* ignore */ }

        // Phase 53, sync the linked account's balance from the statement's
        // closing balance + period_end. Best-effort.
        if (accountId) {
          try {
            const job = await fetchJob(jobId);
            const meta = job?.result?.account || {};
            if (meta.closing_balance != null && meta.statement_period_end) {
              await syncAccountBalanceFromStatement({
                account_id: accountId,
                closing_balance: meta.closing_balance,
                period_end: meta.statement_period_end,
              });
            }
          } catch (e) { /* swallow */ }
        }
      } catch (e) {
        console.error('v2 confirm failed:', e);
        toast.error('Something went wrong finishing the import. Your transactions are still in the ledger — check Transactions to confirm.');
      } finally {
        setImporting(false);
      }
      return;  // <-- short-circuit complete; do NOT fall through to bulk-insert path
    }

    // ------------------------------------------------------------------
    // CSV flow continues below (bulk insert into transactions table).
    // ------------------------------------------------------------------
    setImporting(true);
    setImportProgress({ done: 0, total: activePreview.length });

    // Phase 84.20 (Beta issue #5 — Ghost Imports guard) —
    // If this preview was seeded from a parse job (`_job_id`), verify
    // the job is still in an importable state BEFORE we commit any
    // rows. Previously the client wrote transactions from a stale
    // preview even after the parse job had been deleted, creating
    // "ghost imports" that reappeared out of nowhere. The backend
    // endpoint returns 410 GONE for soft-deleted / aborted jobs.
    const previewJobId = activePreview.find((r) => r._job_id)?._job_id || null;
    if (previewJobId && session?.access_token) {
      try {
        await axios.get(
          `${BACKEND_URL}/api/import/jobs/${previewJobId}/importable`,
          { headers: { Authorization: `Bearer ${session.access_token}` }, timeout: 8000 },
        );
      } catch (e) {
        const st = e?.response?.status;
        if (st === 410 || st === 404 || st === 409) {
          const msg = e?.response?.data?.detail?.message
            || 'This upload was removed and can no longer be imported. Please refresh and re-upload if needed.';
          toast.error(msg, { duration: 9000 });
          setImporting(false);
          setImportProgress(null);
          return;
        }
        // Network / unknown — proceed with a soft warning. We do NOT
        // abort here because a transient backend blip must not block
        // a legitimate user's import.
        console.warn('[ghost-import-guard] check failed, proceeding:', e?.message);
      }
    }

    const inserts = activePreview.map((r) => {
      const base = {
        user_id: user.id,
        txn_date: r.txn_date,
        description: r.description,
        amount: r.amount,
        type: r.type,
        category: r.category,
        source: sourceType === 'pdf' ? 'pdf' : 'csv',
      };
      // Only include account_id when set. If the schema migration hasn't been
      // applied, we retry without it below so users aren't blocked.
      return accountId ? { ...base, account_id: accountId } : base;
    });
    // Phase 17.1 → Phase 59 — chunk size + timeout retained as
    // belt-and-braces, but the per-chunk insert now goes through
    // `insertTransactionsBulkIdempotent` (fingerprint + ON CONFLICT
    // DO NOTHING) so a retried chunk never duplicates rows. The
    // legacy `insertWithTimeout` / `stripIfNeeded` helpers that used
    // to live here are gone — the new helper handles missing-column
    // recovery internally.

    // Phase 59 — idempotent bulk insert. Each row is stamped with a
    // (user_id, account_id, txn_date, abs(amount), normalised_desc)
    // fingerprint and the upsert uses ON CONFLICT DO NOTHING against
    // the unique index. Re-uploading the same CSV is now a true no-op
    // even if the file hash check is somehow bypassed. The 45-second
    // chunk timeout below is left in place as belt-and-braces against
    // a stalled HTTP request — but even if the abort fires while the
    // server has already committed, the retry won't duplicate.
    try {
      let done = 0;
      const CHUNK = 50;
      // We do still need the "missing account_id column" recovery path
      // for legacy schemas, so we drive the upsert one chunk at a time.
      let stripAccountId = false;
      for (let i = 0; i < inserts.length; i += CHUNK) {
        const sliceRaw = inserts.slice(i, i + CHUNK);
        const slice = stripAccountId
          ? sliceRaw.map(({ account_id, ...rest }) => rest)
          : sliceRaw;
        // eslint-disable-next-line no-await-in-loop
        let result = await insertTransactionsBulkIdempotent(slice, { chunkSize: CHUNK });
        // Detect missing account_id column once, strip the field, retry.
        if (
          result.errors.length > 0 &&
          !stripAccountId &&
          result.errors.some((e) => /column .*account_id.* does not exist|PGRST204/i.test(`${e.message} ${e.code || ''}`))
        ) {
          console.warn('transactions.account_id column missing, retrying without it.');
          toast.warning('Heads up: transactions are being imported without account linking. Apply schema_phase17.sql when convenient.', { duration: 9000 });
          stripAccountId = true;
          const sliceNoAcct = slice.map(({ account_id, ...rest }) => rest);
          // eslint-disable-next-line no-await-in-loop
          result = await insertTransactionsBulkIdempotent(sliceNoAcct, { chunkSize: CHUNK });
        }
        if (result.errors.length > 0) {
          // Phase 71.0 — pull out friendly dedup collisions and surface
          // them as a calm toast instead of leaking the raw PG error.
          const dedupErrors = result.errors.filter((e) => e && e.code === 'already_imported' && e.friendly);
          const otherErrors = result.errors.filter((e) => !(e && e.code === 'already_imported' && e.friendly));
          if (otherErrors.length === 0 && dedupErrors.length > 0) {
            // Pure dedup — treat as a partial success.
            toast.info(dedupErrors[0].message, { duration: 9000 });
            // Continue the loop; don't throw.
            done += result.inserted + result.duplicates;
            setImportProgress({ done, total: inserts.length });
            continue;
          }
          throw new Error(otherErrors.map((e) => e.message || e).join('; '));
        }
        done += result.inserted + result.duplicates;
        setImportProgress({ done, total: inserts.length });
      }
      toast.success(`Imported ${activePreview.length} transactions.`);
      summariseImport(activePreview, { count: activePreview.length });
      setStage('done');
      // Phase 71.2 — auto-confirm any recurring rules that just got
      // statement evidence. Fire-and-forget so a slow scan never blocks
      // the success toast. PDF imports also hit this path (via the
      // backend `mark-consumed` hook), so calling it here is idempotent.
      triggerRecurringAutoConfirm(session);
      // If this preview came from a parse job, mark it consumed so the bell stops nagging
      const jobId = activePreview.find((r) => r._job_id)?._job_id;
      if (jobId) {
        try { await markConsumed(jobId); } catch { /* ignore */ }
        // Phase 53, sync the linked account's balance from the statement's
        // closing balance + period_end. Pulls the job again to access
        // result.account meta (the preview only carries trimmed txn rows).
        // Best-effort; failures are swallowed (e.g. migration not yet run).
        if (accountId) {
          try {
            const job = await fetchJob(jobId);
            const meta = job?.result?.account || {};
            if (meta.closing_balance != null && meta.statement_period_end) {
              const r = await syncAccountBalanceFromStatement({
                account_id: accountId,
                closing_balance: meta.closing_balance,
                statement_period_end: meta.statement_period_end,
                filename: job?.filename || null,
              });
              if (r.applied) {
                // Tell the user once; they'll see the new balance on Accounts.
                toast.success(`Account balance updated from this statement.`, { duration: 4500 });
              }
            }
          } catch (e) {
            console.warn('account-balance sync (single import) failed:', e?.message || e);
          }
        }
      }
    } catch (e) {
      console.error('Import failed:', e);
      // Phase 71.0 — never leak raw PG unique-violation text into a toast.
      const raw = String(e?.message || e || '');
      if (/ux_transactions_user_acct_fingerprint|duplicate key value violates|23505/i.test(raw)) {
        toast.info(
          "Some of these transactions are already in your ledger — we'll skip those. Try running the import again.",
          { duration: 9000 },
        );
      } else {
        toast.error(e.message || 'Import failed.', { duration: 10000 });
      }
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-semibold">Import a statement</h1>
          <p className="text-sm text-muted-foreground">Drop a bank statement (PDF or CSV), we&rsquo;ll read it and learn how you categorize, so it gets easier over time.</p>
        </div>
        {stage !== 'upload' && mode === 'single' && (
          <Button variant="ghost" onClick={reset} className="rounded-xl" data-testid="csv-reset-button"><RotateCcw className="h-4 w-4 mr-1" /> Start over</Button>
        )}
      </div>

      {/* Phase 85.1 — debt payment reconciliation for the statement just
          imported. Renders nothing when no loan payments were found. */}
      {debtReconcileImportId && (
        <DebtReconcileSummary importId={debtReconcileImportId} />
      )}

      {/* Phase 44, One-time welcome banner for freshly-converted paying
          customers. Dismissed server-side; never re-appears. */}
      <OnboardingPoolWelcomeBanner
        session={session}
        pool={poolDetail?.pool}
        plan={poolDetail?.plan}
        welcomeSeenAt={poolDetail?.welcome_seen_at}
        onDismissed={() => setPoolDetail((d) => d ? { ...d, welcome_seen_at: new Date().toISOString() } : d)}
      />

      {/* Mode tabs, Single vs Bulk Backfill vs Stored documents (Phase 70G) */}
      <div className="inline-flex items-center gap-1 rounded-2xl border border-border/70 bg-card p-1">
        <button
          onClick={() => setMode('single')}
          data-testid="mode-tab-single"
          className={`px-3.5 py-1.5 rounded-xl text-xs font-medium transition-colors flex items-center gap-1.5 ${mode === 'single' ? 'bg-accent text-accent-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
        >
          <FileText className="h-3.5 w-3.5" /> Single statement
        </button>
        <button
          onClick={() => setMode('bulk')}
          data-testid="mode-tab-bulk"
          className={`px-3.5 py-1.5 rounded-xl text-xs font-medium transition-colors flex items-center gap-1.5 ${mode === 'bulk' ? 'bg-accent text-accent-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
        >
          <Layers className="h-3.5 w-3.5" /> Bulk backfill <Badge variant="outline" className="text-[9px] rounded-full px-1.5 py-0 border-[hsl(var(--gold))]/40 text-[hsl(var(--gold))] ml-1">New</Badge>
        </button>
        <button
          onClick={() => setMode('stored')}
          data-testid="mode-tab-stored"
          className={`px-3.5 py-1.5 rounded-xl text-xs font-medium transition-colors flex items-center gap-1.5 ${mode === 'stored' ? 'bg-accent text-accent-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
        >
          <Shield className="h-3.5 w-3.5" /> Uploaded statements
        </button>
      </div>

      {mode === 'stored' ? (
        <StoredDocumentsPanel />
      ) : mode === 'bulk' ? (
        <BulkBackfillPanel
          plan={plan}
          onTrial={onTrial}
          canParsePdf={canParsePdf}
          session={session}
          user={user}
          currency={currency}
          usage={usage}
          refreshUsage={refreshUsage}
          accounts={accounts}
          accountsLoaded={accountsLoaded}
          reloadAccounts={loadAccounts}
          isBeta={!!profile?.is_beta}
        />
      ) : (
      <SingleImportFlow {...{ stage, setStage, sourceType, setSourceType, fileName, setFileName, headers, setHeaders, rows, setRows, mapping, setMapping, dateFormat, setDateFormat, detectedDateFormat, effectiveDateFormat, previewRows, setPreviewRows, learnDialog, setLearnDialog, fileRef, canParsePdf, currency, previewCurrency, accountCurrency, detectedCurrency, currencyMismatch, handleFile, csvPreview, activePreview, editCategory, saveLearnDialog, confirm, reset, parseError, setParseError, pdfCap, pdfUsed, capExhausted, onTrial, refreshUsage, importing, importProgress, accounts, accountId, setAccountId, accountsLoaded, reloadAccounts: loadAccounts, dupDialog, setDupDialog, proceedDespiteDuplicate, pool, poolActive, poolRemaining, dedupSummary, importResult, visibleInstallmentClusters, clusterKey, applyInstallmentPreset, dismissInstallmentCluster, rowErrors, rowResolutions, setRowResolutions }} />
      )}

      {/* ----- Phase 41, Statement-aware account reconciliation dialog ----
          Opens after a PDF parse completes when an unmatched account is
          confidently detected. Three actions: add new (opens inline create
          dialog with prefilled fields), link existing, or decide later. */}
      <StatementAccountDialog
        open={!!statementAccountDialog}
        onOpenChange={(v) => { if (!v) setStatementAccountDialog(null); }}
        account={statementAccountDialog?.account}
        accounts={accounts}
        txnCount={statementAccountDialog?.txnCount || 0}
        onAddNew={(prefill) => {
          // Hand off to the inline create dialog with prefilled fields.
          setInlineAccountPrefill(prefill);
          setStatementAccountDialog(null);
        }}
        onLinkExisting={(linkId) => {
          setAccountId(linkId);
          const matched = (accounts || []).find((a) => a.id === linkId);
          if (matched) {
            const last4 = matched.last4 ? ` ••${matched.last4}` : '';
            toast.success(`Linked to ${matched.name}${last4}.`, { duration: 4000 });
          }
          setStatementAccountDialog(null);
        }}
        onSkip={() => {
          setStatementAccountDialog(null);
        }}
      />

      {/* Inline "New account" dialog, used by the StatementAccountDialog
          "Add this account" path. The picker has its own local instance
          (without prefill), we keep both so neither flow breaks. */}
      <NewAccountInlineDialog
        open={!!inlineAccountPrefill}
        setOpen={(v) => { if (!v) setInlineAccountPrefill(null); }}
        defaults={inlineAccountPrefill || undefined}
        onCreated={async (newId) => {
          await loadAccounts();
          if (newId) {
            setAccountId(newId);
            toast.success('Account created and linked to this import.', { duration: 4000 });
          }
          setInlineAccountPrefill(null);
        }}
      />

      {/* ----- Phase 42, Locked PDF password prompt ---------------------- */}
      <LockedPdfDialog
        open={!!lockedPdf}
        onOpenChange={(v) => { if (!v) setLockedPdf(null); }}
        filename={lockedPdf?.file?.name}
        errorMessage={lockedPdf?.errorMessage}
        busy={!!lockedPdf?.busy}
        onSubmit={submitLockedPdfPassword}
        onCancel={() => setLockedPdf(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single-statement flow extracted as a sub-component so the page renders
// either it or the BulkBackfill panel based on the mode tab.
// ---------------------------------------------------------------------------
function SingleImportFlow({ stage, sourceType, fileName, headers, rows, mapping, setMapping, dateFormat, setDateFormat, detectedDateFormat, effectiveDateFormat, previewRows, learnDialog, setLearnDialog, fileRef, canParsePdf, currency, previewCurrency, accountCurrency, detectedCurrency, currencyMismatch, handleFile, csvPreview, activePreview, editCategory, saveLearnDialog, confirm, reset, setStage, setPreviewRows, parseError, setParseError, pdfCap, pdfUsed, capExhausted, onTrial, refreshUsage, importing, importProgress, accounts, accountId, setAccountId, accountsLoaded, reloadAccounts, dupDialog, setDupDialog, proceedDespiteDuplicate, pool, poolActive, poolRemaining, dedupSummary, importResult = null, visibleInstallmentClusters = [], clusterKey = () => '', applyInstallmentPreset = () => {}, dismissInstallmentCluster = () => {}, rowErrors = new Map(), rowResolutions = new Map(), setRowResolutions = () => {} }) {
  // v3.5.3 Phase 84.26 — "Show only rows needing review" toggle so users
  // with big imports (300+ rows) can quickly find the ones we couldn't
  // auto-categorize. Also removes the old 300-row render cap that was
  // silently hiding rows 301+ from the categorize UI.
  const [showReviewOnly, setShowReviewOnly] = React.useState(false);
  const reviewCount = React.useMemo(
    () => (activePreview || []).filter((r) => r && r.needs_review && !r._is_duplicate).length,
    [activePreview],
  );
  // Pair rows with their original index BEFORE filtering so editCategory()
  // still targets the correct row in the source array.
  const previewWithIdx = React.useMemo(
    () => (activePreview || []).map((r, origIdx) => ({ r, origIdx })),
    [activePreview],
  );
  const visibleRows = React.useMemo(
    () => (showReviewOnly
      ? previewWithIdx.filter(({ r }) => r && r.needs_review && !r._is_duplicate)
      : previewWithIdx),
    [previewWithIdx, showReviewOnly],
  );
  // v3.5.3 Phase 84.26 — preflight errors summary.
  // v3.6.5 — each bad_row is stored under BOTH stage_row_id and line_no
  // keys so the row-render lookup can hit either one. Count unique bads
  // by de-duping on the meta object we stored (same reference for both
  // keys → Set() reduces to N).
  // v3.6.7 Phase 84.28.5 — resolved rows (user picked Keep or Drop) no
  // longer count toward "N rows need fixing" — the user has addressed
  // them so the counter must reflect the remaining unresolved rows only.
  const errorCount = (() => {
    if (!rowErrors) return 0;
    const unresolved = new Set();
    for (const meta of rowErrors.values()) {
      const sid = meta?.stage_row_id;
      if (sid && rowResolutions.get(sid)) continue;
      unresolved.add(meta);
    }
    return unresolved.size;
  })();
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 text-xs">
        {['Upload', sourceType === 'pdf' ? 'Reading' : 'Map columns', 'Preview', 'Done'].map((label, i) => {
          const idx = i;
          const cur = ['upload', sourceType === 'pdf' ? 'parsing' : 'mapping', 'preview', 'done'].indexOf(stage);
          const active = idx === cur;
          const passed = idx < cur || stage === 'done';
          return (
            <React.Fragment key={label}>
              <div className={`px-3 py-1.5 rounded-full border ${active ? 'border-primary bg-accent text-accent-foreground' : passed ? 'border-border/70 bg-card text-muted-foreground' : 'border-border/40 text-muted-foreground'}`}>{label}</div>
              {i < 3 && <div className="h-px flex-1 bg-border/60" />}
            </React.Fragment>
          );
        })}
      </div>

      {stage === 'upload' && (
        <>
          {/* Persistent cap status banner (only if PDF parsing is allowed and we know cap) */}
          {canParsePdf && pdfCap != null && (
            <Card
              data-testid="pdf-cap-pill"
              className={`rounded-2xl border ${
                capExhausted
                  ? 'border-destructive/40 bg-destructive/5'
                  : poolActive
                  ? 'border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/5'
                  : 'border-border/70'
              }`}
            >
              <CardContent className="p-3.5 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2.5 text-xs">
                  {capExhausted ? (
                    <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
                  ) : (
                    <Sparkles className="h-4 w-4 text-[hsl(var(--gold))] shrink-0" />
                  )}
                  <div>
                    <div className="font-medium">
                      {capExhausted
                        ? (poolActive ? 'Setup imports exhausted' : 'Monthly PDF parsing cap reached')
                        : poolActive
                        ? `Setup imports, ${pool.used} of ${pool.total} used`
                        : `PDF parses used this month: ${pdfUsed} / ${pdfCap}`}
                    </div>
                    {capExhausted ? (
                      <div className="text-muted-foreground text-[11px] mt-0.5">
                        {poolActive
                          ? `Your monthly cap is ${pdfCap} PDFs. Or add headroom from /pricing.`
                          : 'Resets on the 1st of next month. CSV imports continue to work.'}
                      </div>
                    ) : poolActive ? (
                      <div className="text-muted-foreground text-[11px] mt-0.5">
                        {poolRemaining} import{poolRemaining === 1 ? '' : 's'} remaining
                        {pool?.days_left != null ? ` · ${pool.days_left} day${pool.days_left === 1 ? '' : 's'} left in your onboarding window` : ''}
                        . Use these to backfill your financial history.
                      </div>
                    ) : (
                      <div className="text-muted-foreground text-[11px] mt-0.5">
                        {pdfCap - pdfUsed} parse{(pdfCap - pdfUsed) === 1 ? '' : 's'} remaining on your {onTrial ? 'Horizon trial' : 'plan'}.
                      </div>
                    )}
                  </div>
                </div>
                {capExhausted && (
                  <Link to="/pricing"><Button size="sm" className="rounded-xl h-7" data-testid="single-cap-upgrade">View plans</Button></Link>
                )}
              </CardContent>
            </Card>
          )}
          {parseError && (
            <Card className={`rounded-2xl border ${parseError.kind === 'cap' ? 'border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/8' : parseError.kind === 'network' ? 'border-border/70 bg-card' : parseError.kind === 'already-imported' ? 'border-emerald-500/40 bg-emerald-500/5' : parseError.kind === 'holdings_snapshot' ? 'border-amber-500/40 bg-amber-500/5' : 'border-destructive/40 bg-destructive/5'}`} data-testid="single-parse-error">
              <CardContent className="p-4 flex items-start gap-3">
                {parseError.kind === 'already-imported'
                  ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
                  : <AlertTriangle className={`h-4 w-4 mt-0.5 shrink-0 ${parseError.kind === 'cap' ? 'text-[hsl(var(--gold))]' : parseError.kind === 'network' ? 'text-muted-foreground' : parseError.kind === 'holdings_snapshot' ? 'text-amber-600 dark:text-amber-400' : 'text-destructive'}`} />
                }
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{parseError.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{parseError.detail}</div>
                  {parseError.status && (
                    <div className="text-[10px] text-muted-foreground mt-1.5">Code: {parseError.status}</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {parseError.kind === 'cap' && (
                    <Link to="/pricing"><Button size="sm" className="rounded-xl h-7" data-testid="single-upgrade-from-cap">View plans</Button></Link>
                  )}
                  {parseError.kind === 'network' && (
                    <Button size="sm" variant="secondary" className="rounded-xl h-7" onClick={() => { setParseError(null); fileRef.current?.click(); }} data-testid="single-network-retry">Try again</Button>
                  )}
                  {parseError.kind === 'already-imported' && (
                    <Link to="/transactions"><Button size="sm" className="rounded-xl h-7" data-testid="single-go-to-transactions">View transactions</Button></Link>
                  )}
                  <button onClick={() => setParseError(null)} className="text-muted-foreground hover:text-foreground p-1" aria-label="Dismiss"><X className="h-4 w-4" /></button>
                </div>
              </CardContent>
            </Card>
          )}
          {/* Phase 17, Account picker (required before upload). Renders inline
              "add an account" prompt if the user has none yet. */}
          <AccountPickerCard
            accounts={accounts}
            accountId={accountId}
            setAccountId={setAccountId}
            accountsLoaded={accountsLoaded}
            reloadAccounts={reloadAccounts}
            capExhausted={capExhausted && canParsePdf}
          />
        <Card className="rounded-2xl border-border/70">
          <CardContent className="p-6">
            {capExhausted && canParsePdf ? (
              <div className="rounded-2xl border border-dashed border-border/60 bg-muted/30 p-10 text-center cursor-not-allowed" data-testid="csv-upload-dropzone-disabled" aria-disabled="true">
                <AlertTriangle className="h-8 w-8 mx-auto text-muted-foreground/70" />
                <div className="mt-3 font-display text-lg font-semibold text-muted-foreground">Cap reached for this month</div>
                <div className="text-sm text-muted-foreground/80">PDF uploads are paused until your monthly limit resets.</div>
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Link to="/pricing"><Button size="sm" className="rounded-xl" data-testid="single-dropzone-cap-upgrade">View plans</Button></Link>
                </div>
                <div className="text-[11px] text-muted-foreground/70 mt-3">CSV imports continue to work , they don&rsquo;t count against your AI limit.</div>
              </div>
            ) : !accountId ? (
              <div className="rounded-2xl border border-dashed border-border/60 bg-muted/20 p-10 text-center cursor-not-allowed" data-testid="csv-upload-dropzone-no-account" aria-disabled="true">
                <Wallet className="h-8 w-8 mx-auto text-muted-foreground/70" />
                <div className="mt-3 font-display text-lg font-semibold text-muted-foreground">Choose an account first</div>
                <div className="text-sm text-muted-foreground/80 max-w-md mx-auto">Pick the account this statement belongs to above , we&rsquo;ll link every imported transaction to it.</div>
              </div>
            ) : (
              <label className="block rounded-2xl border border-dashed border-border/70 bg-card hover:bg-accent/30 transition-colors p-10 text-center cursor-pointer" data-testid="csv-upload-dropzone">
                <UploadCloud className="h-8 w-8 mx-auto text-muted-foreground" />
                <div className="mt-3 font-display text-lg font-semibold">Drop your statement here</div>
                <div className="text-sm text-muted-foreground">PDF, CSV, OFX, or XLSX — or click to browse</div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,.pdf,.ofx,.qfx,.xlsx,text/csv,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="csv-file-input"
                />
              </label>
            )}
            <div className="grid sm:grid-cols-2 gap-3 mt-4 text-xs text-muted-foreground">
              <div className="flex items-start gap-2 rounded-xl border border-border/60 px-3 py-2.5">
                <FileType2 className="h-4 w-4 mt-0.5 text-primary" />
                <div>
                  <div className="text-foreground/80 font-medium text-[13px]">PDF statements</div>
                  <div className="text-[11.5px] mt-0.5">{canParsePdf
                    ? 'Read by AI. Works with statements from most major banks worldwide.'
                    : <span>PDF parsing is on Clarity and above. <Link to="/pricing" className="text-primary hover:underline">View plans</Link>.</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2 rounded-xl border border-border/60 px-3 py-2.5">
                <Brain className="h-4 w-4 mt-0.5 text-[hsl(var(--gold))]" />
                <div>
                  <div className="text-foreground/80 font-medium text-[13px]">Learns as you go</div>
                  <div className="text-[11.5px] mt-0.5">Edit a category once, next statement, the same merchant categorizes itself.</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        </>
      )}

      {stage === 'parsing' && (
        <Card className="rounded-2xl border-border/70">
          <CardContent className="p-10 text-center space-y-3">
            <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="h-4 w-4 text-[hsl(var(--gold))] animate-pulse" />
              Reading <span className="font-medium text-foreground">{fileName}</span>&hellip;
            </div>
            <div className="text-xs text-muted-foreground max-w-md mx-auto">Bank statements can take 30&ndash;60 seconds for the first read. We&rsquo;ll show you a preview before anything gets saved.</div>
          </CardContent>
        </Card>
      )}

      {stage === 'submitted' && (
        <Card className="rounded-2xl border-border/70" data-testid="single-submitted-card">
          <CardContent className="p-10 text-center space-y-3">
            <div className="inline-flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              <span className="font-medium">Statement queued</span>
            </div>
            <div className="text-sm text-muted-foreground max-w-md mx-auto">
              We&rsquo;re reading <span className="font-medium text-foreground">{fileName}</span> in the background.
              You can keep using 2120 , we&rsquo;ll show a gentle notification in the
              <Bell className="inline h-3.5 w-3.5 mx-1 -mt-0.5" />
              bell when it&rsquo;s ready.
            </div>
            <div className="text-[11px] text-muted-foreground">
              Usually 15&ndash;30 seconds. Statements with many pages may take longer.
            </div>
            <div className="flex items-center justify-center gap-2 pt-1">
              <Button variant="secondary" onClick={reset} className="rounded-xl" data-testid="single-submitted-import-another"><RotateCcw className="h-4 w-4 mr-1" /> Import another</Button>
              <Link to="/dashboard"><Button className="rounded-xl">Back to dashboard <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
            </div>
          </CardContent>
        </Card>
      )}

      {stage === 'mapping' && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="rounded-2xl border-border/70">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-muted-foreground" /><div className="text-sm font-medium">{fileName || 'Mapping'}</div></div>
              <div className="text-xs text-muted-foreground">Tell us what each column means.</div>
              <div className="space-y-2">
                {headers.map((h) => (
                  <div key={h} className="flex items-center justify-between gap-3 rounded-xl border border-border/70 px-3 py-2">
                    <div className="text-sm truncate">{h}</div>
                    <Select value={mapping[h] || '__skip'} onValueChange={(v) => setMapping({ ...mapping, [h]: v })}>
                      <SelectTrigger className="w-[180px] rounded-xl" data-testid={`csv-mapping-select-${h}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{FIELD_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              {/* Phase 71C — date format picker. Only surfaced when a date
                  column is actually mapped. Defaults to "Auto" which uses
                  detectDateFormat() to pick MDY vs DMY from the data
                  itself. Manual override is available when the file is
                  ambiguous (every value's day & month ≤ 12). */}
              {Object.values(mapping).includes('date') && (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-border/70 px-3 py-2 bg-muted/30" data-testid="csv-date-format-row">
                  <div className="min-w-0">
                    <div className="text-sm">Date format</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {dateFormat === DATE_FMT_AUTO
                        ? (detectedDateFormat === DATE_FMT_MDY ? 'Detected: US (MM/DD/YYYY)'
                           : detectedDateFormat === DATE_FMT_DMY ? 'Detected: India / EU (DD/MM/YYYY)'
                           : detectedDateFormat === DATE_FMT_YMD ? 'Detected: ISO (YYYY-MM-DD)'
                           : 'Ambiguous — defaulting to DD/MM/YYYY. Override if needed.')
                        : 'Manual override — auto-detect off.'}
                    </div>
                  </div>
                  <Select value={dateFormat} onValueChange={setDateFormat}>
                    <SelectTrigger className="w-[200px] rounded-xl" data-testid="csv-date-format-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={DATE_FMT_AUTO}>Auto-detect</SelectItem>
                      <SelectItem value={DATE_FMT_MDY}>US · MM/DD/YYYY</SelectItem>
                      <SelectItem value={DATE_FMT_DMY}>India / EU · DD/MM/YYYY</SelectItem>
                      <SelectItem value={DATE_FMT_YMD}>ISO · YYYY-MM-DD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button onClick={() => { setPreviewRows(csvPreview); setStage('preview'); }} className="rounded-xl w-full" data-testid="csv-go-preview-button">Preview</Button>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-border/70">
            <CardContent className="p-5">
              <div className="text-sm font-medium mb-3">First 5 raw rows</div>
              <div className="overflow-auto max-h-96 rounded-xl border border-border/60">
                <Table>
                  <TableHeader><TableRow>{headers.map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
                  <TableBody>
                    {rows.slice(0, 5).map((r, i) => (
                      <TableRow key={i}>
                        {headers.map((h) => <TableCell key={h} className="text-xs">{r[h]}</TableCell>)}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {stage === 'preview' && (
        <Card className="rounded-2xl border-border/70">
          <CardContent className="p-5 space-y-3">
            {/* v3.19.2 — a statement opened from the bell can arrive with no
                account mapped (it was queued before one was chosen). The
                picker used to live only on the upload stage, so there was no
                way to supply it here and the import could only be refused. */}
            {!accountId && (
              <div
                className="rounded-xl border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/5 p-3 space-y-2"
                data-testid="preview-account-required"
              >
                <div className="text-sm font-medium">Which account is this statement from?</div>
                <div className="text-[11px] text-muted-foreground">
                  Every transaction below will be linked to it. We can&rsquo;t import without this.
                </div>
                <AccountPickerCard
                  accounts={accounts}
                  accountId={accountId}
                  setAccountId={setAccountId}
                  accountsLoaded={accountsLoaded}
                  reloadAccounts={reloadAccounts}
                  capExhausted={false}
                />
              </div>
            )}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-sm font-medium">
                  Preview &middot; {activePreview.length} transactions
                  {reviewCount > 0 && (
                    <span className="ml-2 text-[11px] font-normal text-[hsl(var(--gold))]" data-testid="preview-review-count">
                      · {reviewCount} need review
                    </span>
                  )}
                  {errorCount > 0 && (
                    <span className="ml-2 text-[11px] font-medium text-destructive inline-flex items-center gap-1" data-testid="preview-error-count">
                      <AlertTriangle className="h-3 w-3" />
                      {errorCount} {errorCount === 1 ? 'row needs fixing' : 'rows need fixing'}
                    </span>
                  )}
                  {/* v3.6.8 Phase 84.28.6 — undo summary strip.
                      Whenever the user has resolved any red-pill row
                      (Keep or Drop), we surface a compact inline
                      summary so a wrong click is easy to spot before
                      they hit Import. */}
                  {(() => {
                    let kept = 0, dropped = 0;
                    for (const v of rowResolutions.values()) {
                      if (v === 'keep') kept++;
                      else if (v === 'drop') dropped++;
                    }
                    if (kept + dropped === 0) return null;
                    return (
                      <span
                        className="ml-2 text-[11px] font-medium inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-2 py-0.5"
                        data-testid="preview-resolutions-summary"
                      >
                        <Sparkles className="h-3 w-3" />
                        {kept > 0 && <span data-testid="preview-resolutions-kept">{kept} kept</span>}
                        {kept > 0 && dropped > 0 && <span className="text-primary/50">·</span>}
                        {dropped > 0 && <span data-testid="preview-resolutions-dropped">{dropped} dropped</span>}
                        <button
                          type="button"
                          onClick={() => setRowResolutions(new Map())}
                          className="text-primary/70 hover:text-primary underline underline-offset-2 ml-1"
                          data-testid="preview-resolutions-undo-all"
                          title="Clear every Keep / Drop choice on this preview"
                        >
                          undo all
                        </button>
                      </span>
                    );
                  })()}
                </div>
                <div className="text-[11px] text-muted-foreground">Edit any category , we&rsquo;ll remember it for next time.</div>
              </div>
              <div className="flex items-center gap-2">
                {reviewCount > 0 && (
                  <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer select-none" data-testid="preview-review-only-toggle-label">
                    <input
                      type="checkbox"
                      checked={showReviewOnly}
                      onChange={(e) => setShowReviewOnly(e.target.checked)}
                      className="h-3.5 w-3.5"
                      data-testid="preview-review-only-toggle"
                    />
                    Show only rows needing review
                  </label>
                )}
                {sourceType === 'csv' && <Button variant="ghost" className="rounded-xl" onClick={() => setStage('mapping')}>Back to mapping</Button>}
                <Button
                  className="rounded-xl"
                  onClick={confirm}
                  disabled={importing || currencyMismatch || !accountId}
                  data-testid="csv-confirm-import-button"
                  title={!accountId
                    ? 'Choose the account this statement belongs to first.'
                    : currencyMismatch
                    ? `This statement appears to be in ${detectedCurrency}, but the selected account is in ${accountCurrency}. Choose a matching account or update the account currency before importing.`
                    : undefined}
                >
                  {importing
                    ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Importing {importProgress.done}/{importProgress.total}&hellip;</>)
                    : (<><Sparkles className="h-4 w-4 mr-1" /> Import {activePreview.length}</>)}
                </Button>
              </div>
            </div>
            {/* H1 · currency-mismatch warning. Shown only when both signals
                are known AND unequal. Changing the account instantly clears
                this and re-enables Import via natural React re-render. */}
            {/* v3.5.3 Phase 84.26 — heads-up banner for large imports so
                users know up front that a longer wait is expected. */}
            {activePreview.length > 400 && (
              <div
                className="flex items-start gap-2 rounded-xl border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/5 px-3 py-2 text-xs"
                data-testid="preview-big-import-banner"
              >
                <Info className="h-3.5 w-3.5 mt-0.5 text-[hsl(var(--gold))] shrink-0" />
                <div className="min-w-0">
                  <span className="font-medium">Large statement — {activePreview.length} rows.</span>{' '}
                  <span className="text-muted-foreground">
                    We&rsquo;ll commit in batches of 250 to stay within the timeout. This can take up to 90 seconds — please keep this tab open.
                  </span>
                </div>
              </div>
            )}
            {currencyMismatch && (
              <div
                className="flex items-start gap-2 rounded-xl border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/5 px-3 py-2 text-xs"
                data-testid="preview-currency-mismatch-banner"
              >
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-[hsl(var(--gold))] shrink-0" />
                <div className="min-w-0">
                  <span className="font-medium">
                    This statement appears to be in {detectedCurrency}, but the selected account is in {accountCurrency}.
                  </span>{' '}
                  <span className="text-muted-foreground">
                    Choose an account with the matching currency or update the account currency before importing.
                  </span>
                </div>
              </div>
            )}
            {/* Phase 54 hotfix v2.7.x, fingerprint-based dedup summary.
                Tells the user, calmly and once, that we noticed overlap with
                their existing ledger and didn't double-import. The skipped
                rows are still shown in the table below with a 'duplicate'
                badge so there's no opaque magic. */}
            {dedupSummary && dedupSummary.duplicates_skipped > 0 && (
              <div
                className="flex items-start gap-2 rounded-xl border border-border/60 bg-muted/40 px-3 py-2 text-xs"
                data-testid="preview-dedup-banner"
              >
                <Info className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <span className="font-medium">
                    {dedupSummary.duplicates_skipped} {dedupSummary.duplicates_skipped === 1 ? 'transaction was' : 'transactions were'} already in your ledger and weren&rsquo;t added again.
                  </span>
                  <span className="text-muted-foreground">
                    {' '}They&rsquo;re still shown below with a <em>duplicate</em> tag for transparency.
                    {dedupSummary.inserted > 0 && ` ${dedupSummary.inserted} new ${dedupSummary.inserted === 1 ? 'row was' : 'rows were'} imported successfully.`}
                  </span>
                </div>
              </div>
            )}
            {visibleInstallmentClusters.length > 0 && (
              <div className="flex flex-col gap-2" data-testid="installment-plan-cards">
                {visibleInstallmentClusters.map((cluster, i) => (
                  <InstallmentPlanCard
                    key={clusterKey(cluster)}
                    cluster={cluster}
                    index={i}
                    onApply={() => applyInstallmentPreset(cluster)}
                    onDismiss={() => dismissInstallmentCluster(cluster)}
                  />
                ))}
              </div>
            )}
            <div className="overflow-auto max-h-[60vh] rounded-xl border border-border/60" data-testid="csv-preview-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead>Category</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleRows.map(({ r, origIdx }) => {
                    const i = origIdx;
                    const pretty = prettifyDescription(r.description);
                    const title = pretty.display || r.description || '';
                    const isDup = !!r._is_duplicate;
                    // v3.5.3 Phase 84.26 — preflight per-row error lookup.
                    // Line numbers are 1-indexed on the backend but the
                    // preview array is 0-indexed. Try both to be safe.
                    // v3.6.5 — prefer the stable stage_row_id key when
                    // available; fall back to line_no lookups for legacy
                    // rows or CSV imports without stage rows.
                    const rowErr = (r._stage_row_id && rowErrors.get(`sr:${r._stage_row_id}`))
                                 || (r._line_no != null && rowErrors.get(r._line_no))
                                 || rowErrors.get(i + 1)
                                 || rowErrors.get(i)
                                 || null;
                    return (
                    <TableRow
                      key={origIdx}
                      className={
                        rowErr ? 'bg-destructive/5 border-l-2 border-destructive'
                        : isDup ? 'bg-muted/30 opacity-60'
                        : r.needs_review ? 'bg-[hsl(var(--gold))]/5'
                        : ''
                      }
                      data-testid={rowErr ? `preview-row-error-${i}` : (isDup ? `preview-row-duplicate-${i}` : `preview-row-${i}`)}
                    >
                      <TableCell className="text-xs tabular-nums whitespace-nowrap">
                        {r.txn_date}
                        {rowErr && (() => {
                          // v3.6.7 Phase 84.28.5 — resolved rows show a
                          // calm "kept" or "dropped" chip instead of the
                          // scary red pill, plus an undo affordance.
                          const resolution = r._stage_row_id ? rowResolutions.get(r._stage_row_id) : null;
                          if (resolution === 'keep') {
                            return (
                              <div className="mt-1 flex items-center gap-1.5" data-testid={`preview-row-resolution-${i}`}>
                                <div className="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 px-1.5 py-0.5 text-[10px] font-medium">
                                  <Check className="h-2.5 w-2.5" />
                                  kept
                                </div>
                                <button
                                  type="button"
                                  className="text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-2"
                                  onClick={() => setRowResolutions((prev) => {
                                    const next = new Map(prev);
                                    next.delete(r._stage_row_id);
                                    return next;
                                  })}
                                  data-testid={`preview-row-undo-${i}`}
                                >undo</button>
                              </div>
                            );
                          }
                          if (resolution === 'drop') {
                            return (
                              <div className="mt-1 flex items-center gap-1.5" data-testid={`preview-row-resolution-${i}`}>
                                <div className="inline-flex items-center gap-1 rounded-md bg-muted text-muted-foreground px-1.5 py-0.5 text-[10px] font-medium">
                                  <X className="h-2.5 w-2.5" />
                                  dropped
                                </div>
                                <button
                                  type="button"
                                  className="text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-2"
                                  onClick={() => setRowResolutions((prev) => {
                                    const next = new Map(prev);
                                    next.delete(r._stage_row_id);
                                    return next;
                                  })}
                                  data-testid={`preview-row-undo-${i}`}
                                >undo</button>
                              </div>
                            );
                          }
                          // Unresolved — show the red pill + Keep/Drop buttons.
                          const isDupError = rowErr.reason === 'duplicate_fingerprint_in_batch';
                          return (
                            <div className="mt-1 space-y-1">
                              <div
                                className="inline-flex items-center gap-1 rounded-md bg-destructive/10 text-destructive px-1.5 py-0.5 text-[10px] font-medium"
                                title={rowErr.hint}
                                data-testid={`preview-row-error-pill-${i}`}
                              >
                                <AlertTriangle className="h-2.5 w-2.5" />
                                {rowErr.reason.replaceAll('_', ' ')}
                              </div>
                              {r._stage_row_id && (
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {isDupError && (
                                    <button
                                      type="button"
                                      onClick={() => setRowResolutions((prev) => {
                                        const next = new Map(prev);
                                        next.set(r._stage_row_id, 'keep');
                                        return next;
                                      })}
                                      className="rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-medium transition-colors"
                                      title="Keep this row anyway — we'll append a small marker so both rows commit."
                                      data-testid={`preview-row-keep-${i}`}
                                    >
                                      Keep anyway
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => setRowResolutions((prev) => {
                                      const next = new Map(prev);
                                      next.set(r._stage_row_id, 'drop');
                                      return next;
                                    })}
                                    className="rounded-md border border-border/70 bg-card hover:bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:text-foreground transition-colors"
                                    title="Drop this row from the import — the rest still import."
                                    data-testid={`preview-row-drop-${i}`}
                                  >
                                    Drop
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-xs truncate max-w-[320px]">
                        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                          <span
                            className={`truncate font-medium ${isDup ? 'line-through' : ''}`}
                            title={pretty.raw || title}
                            data-testid={`preview-desc-${i}`}
                          >
                            {title}
                          </span>
                          {pretty.mode && (
                            <Badge
                              variant="outline"
                              className="rounded-full text-[10px] px-1.5 py-0 shrink-0 uppercase tracking-wide"
                              data-testid={`preview-mode-${i}`}
                            >
                              {modeLabel(pretty.mode)}
                            </Badge>
                          )}
                          {isDup && (
                            <Badge
                              variant="outline"
                              className="rounded-full text-[10px] px-1.5 py-0 shrink-0 border-muted-foreground/30 text-muted-foreground"
                              title="Already in your ledger — not imported again"
                              data-testid={`preview-duplicate-badge-${i}`}
                            >
                              duplicate
                            </Badge>
                          )}
                          {r.needs_review && !isDup && (
                            <Badge variant="outline" className="rounded-full text-[10px] px-1.5 py-0 border-[hsl(var(--gold))]/40 text-[hsl(var(--gold))] shrink-0" title="Please review this row">review</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        <CategorySelect
                          type={r.type || 'expense'}
                          value={r.category || 'Miscellaneous'}
                          onChange={(v) => editCategory(i, v)}
                          disabled={isDup}
                          triggerClassName="w-[170px] h-7 rounded-lg text-[11px] px-2"
                          contentClassName="max-h-[50vh]"
                          testId={`preview-category-${i}`}
                        />
                        {r.category_source === 'learned' && !isDup && (
                          <Badge variant="secondary" className="ml-1.5 rounded-full text-[10px] px-1.5 py-0">learned</Badge>
                        )}
                        {r.category_source === 'needs_review' && !isDup && (
                          <Badge variant="outline" className="ml-1.5 rounded-full text-[10px] px-1.5 py-0 border-[hsl(var(--gold))]/40 text-[hsl(var(--gold))]" title="The AI suggested a category we don't recognize — please confirm.">review</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{r.type}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{formatMoney(r.amount, previewCurrency, { compact: true })}</TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {showReviewOnly && (
              <div className="text-xs text-muted-foreground" data-testid="preview-review-filter-caption">
                Showing {visibleRows.length} of {activePreview.length} rows that need review. All {activePreview.length} rows (including auto-categorized ones) will still be imported.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {stage === 'done' && (
        <Card className="rounded-2xl border-border/70" data-testid="import-done-card">
          <CardContent className="p-8 text-center space-y-3">
            <CheckCircle2 className="h-8 w-8 text-primary mx-auto" />
            <div className="font-display text-xl font-semibold" data-testid="import-done-headline">
              {importResult
                ? `${importResult.count} transaction${importResult.count === 1 ? '' : 's'} added${importResult.accountName ? ` to ${importResult.accountName}` : ''}`
                : 'Import complete'}
              {importResult?.accountLast4 && (
                <span className="text-muted-foreground font-normal tabular-nums text-base ml-1.5">
                  &bull;&bull;{importResult.accountLast4}
                </span>
              )}
            </div>
            {importResult ? (
              <div className="text-sm text-muted-foreground space-y-1">
                {(importResult.from || importResult.to) && (
                  <div data-testid="import-done-range">
                    {importResult.from === importResult.to
                      ? importResult.from
                      : `${importResult.from} → ${importResult.to}`}
                    {importResult.fileName ? ` · from ${importResult.fileName}` : ''}
                  </div>
                )}
                {importResult.skipped > 0 && (
                  <div data-testid="import-done-skipped">
                    {importResult.skipped} row{importResult.skipped === 1 ? '' : 's'} skipped — already in your ledger or deselected.
                  </div>
                )}
                {importResult.count === 0 && (
                  <div>Nothing new to add — every row was already in your ledger.</div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Your transactions are now part of your 2120 picture.</p>
            )}
            <div className="flex items-center justify-center gap-2 flex-wrap pt-1">
              <Button variant="secondary" onClick={reset} className="rounded-xl" data-testid="import-done-another">Import another file</Button>
              <Link
                to={(() => {
                  const q = new URLSearchParams();
                  if (importResult?.accountId) q.set('account', importResult.accountId);
                  if (importResult?.from) q.set('from', importResult.from);
                  if (importResult?.to)   q.set('to', importResult.to);
                  return `/transactions${q.toString() ? `?${q}` : ''}`;
                })()}
              >
                <Button className="rounded-xl" data-testid="import-done-view-transactions">
                  View in transactions <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
            <Link
              to="/dashboard"
              className="inline-block text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
              data-testid="csv-done-goto-dashboard"
            >
              Go to dashboard instead
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Phase 17, Duplicate statement warning dialog */}
      <DuplicateWarningDialog
        dupDialog={dupDialog}
        setDupDialog={setDupDialog}
        proceed={proceedDespiteDuplicate}
        accounts={accounts}
      />

      {/* Learn-rule dialog (v3.4.2 — extracted to LearnRuleDialog for
          local state, kills the cursor lag on the Pattern input caused by
          re-rendering the entire SingleImportFlow on every keystroke). */}
      <LearnRuleDialog
        trigger={learnDialog}
        onClose={() => setLearnDialog(null)}
        onSave={saveLearnDialog}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// BulkBackfillPanel, multi-PDF queue with sequential processing.
//   • Trial: hard cap = 4 PDFs
//   • Paid (Clarity+): up to 12 PDFs
//   • Per-file status + overall progress
//   • On completion: trigger snapshot + reflection backfill for affected months
// ---------------------------------------------------------------------------
function BulkBackfillPanel({ plan, onTrial, canParsePdf, session, user, currency, usage, refreshUsage, accounts, accountsLoaded, reloadAccounts, isBeta }) {
  // Phase 49, beta users get the full 12-month backfill cap, same as
  // paid users. They never see "trial limit" framing.
  const TRIAL_MAX = 4;
  const PAID_MAX = 12;
  const maxFiles = (isBeta || !onTrial) ? PAID_MAX : TRIAL_MAX;
  const trialCapped = onTrial && !isBeta;

  // Phase 17, each queue entry carries its own account_id. New additions
  // default to the most-recently chosen account so backfilling 12 months of the
  // same account requires only one selection.
  // Phase 50, queue / running / done-summary / batch-password now live in
  // the BulkImportContext at App level so navigating away mid-batch
  // doesn't unmount the work. Re-mounting this panel after navigation
  // restores the queue + in-flight progress + final summary.
  const {
    queue, setQueue, updateItem: updateItemCtx, removeItem: removeItemCtx, clearQueue,
    running, setRunning,
    doneSummary, setDoneSummary,
    batchPasswordRef,
    passwordCacheRef,
  } = useBulkImport();
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  // Phase 51, when the user clicks "Create account" on a queue row, we
  // open the inline dialog pre-filled with detected last4 + bank, and
  // remember which row to attach the new account to on creation.
  const [newAccountPrefill, setNewAccountPrefill] = useState(null);
  const [newAccountForFile, setNewAccountForFile] = useState(null);
  // Phase 49, encrypted-PDF prompt for bulk flow. We resolve `pendingPasswordResolver`
  // when the user submits or cancels the LockedPdfDialog.
  const [lockedPdf, setLockedPdf] = useState(null); // { item, errorMessage, busy }
  const passwordResolverRef = useRef(null);
  const fileRef = useRef(null);
  const { enqueue: enqueueJob } = useParseJobs({ noPoll: true });

  // Phase 49, open the LockedPdfDialog and await the user's password.
  // Resolves to a string password (success), or null (user cancelled).
  const askForPdfPassword = (item, errorMessage = null) => {
    return new Promise((resolve) => {
      passwordResolverRef.current = resolve;
      setLockedPdf({ item, errorMessage, busy: false });
    });
  };
  const onPasswordSubmit = (password) => {
    setLockedPdf((prev) => prev ? { ...prev, busy: true } : prev);
    if (passwordResolverRef.current) {
      const resolve = passwordResolverRef.current;
      passwordResolverRef.current = null;
      resolve(password);
    }
    setLockedPdf(null);
  };
  const onPasswordCancel = () => {
    if (passwordResolverRef.current) {
      const resolve = passwordResolverRef.current;
      passwordResolverRef.current = null;
      resolve(null);
    }
    setLockedPdf(null);
  };

  // Phase 51, Default-account picker removed. Account assignment is
  // driven 100% by per-file detection (filename → content peek → user pick).
  // The batch no longer carries a single "default" account.

  const pdfCap = usage?.caps?.pdf_parse ?? null;
  const pdfUsed = usage?.used?.pdf_parse ?? 0;
  const pdfRemaining = pdfCap == null ? null : Math.max(0, pdfCap - pdfUsed);
  // Phase 44, Onboarding pool overrides the monthly cap during the window.
  const pool = usage?.onboarding_pool || null;
  const poolActive = Boolean(pool?.active);
  const poolRemaining = pool?.remaining ?? 0;
  const effectiveRemaining = poolActive ? poolRemaining : pdfRemaining;
  const capExhausted = effectiveRemaining === 0;

  // Effective queue cap = lesser of plan/trial UX cap and remaining AI parses
  const effectiveMax = effectiveRemaining != null ? Math.min(maxFiles, effectiveRemaining) : maxFiles;
  const canAddMore = canParsePdf && !capExhausted && queue.length < effectiveMax && !running;

  // Phase 51, Encrypted-PDF helpers used during drop-time peek.
  //
  // tryPeekWithPasswordCache: For an encrypted file we already KNOW is
  // locked, attempt the server peek with each password the user has
  // typed earlier in this batch. Returns the peek response on first
  // success (i.e. server returns ok=true with decrypted=true), or null
  // if no cached password unlocks it.
  const tryPeekWithPasswordCache = async (file, b64) => {
    const candidates = passwordCacheRef.current || [];
    for (const pw of candidates) {
      try {
        const r = await axios.post(
          `${BACKEND_URL}/api/import/peek-account`,
          { file_base64: b64, filename: file.name, pdf_password: pw },
          { headers: { Authorization: `Bearer ${session?.access_token}` }, timeout: 45000 },
        );
        if (r.data?.ok) {
          // Remember which password actually worked, used by the
          // submission path so it doesn't retry the whole cache.
          batchPasswordRef.current = pw;
          return { data: r.data, password: pw };
        }
      } catch (_) { /* try next */ }
    }
    return null;
  };

  // fileToBase64: Convert a File → base64 string without blowing memory
  // on the larger statements (some banks are 5–10 MB).
  const fileToBase64 = async (file) => {
    const arr = new Uint8Array(await file.arrayBuffer());
    let bin = '';
    const chunk = 0x8000;
    for (let i = 0; i < arr.length; i += chunk) {
      bin += String.fromCharCode.apply(null, arr.subarray(i, i + chunk));
    }
    return btoa(bin);
  };

  const handleAddFiles = async (files) => {
    if (!files || !files.length) return;
    if (!canParsePdf) {
      toast.error('Bulk backfill needs PDF parsing, available on Clarity and above.');
      return;
    }
    if (capExhausted) {
      toast.error(`You’ve used all ${pdfCap} PDF parses this month. Resets on the 1st, or upgrade.`);
      return;
    }
    // Phase 51, Zero-account guard. The user must have at least one
    // account on file before we can match anything. We still let them add
    // files; we just nudge them to add their first account first.
    if ((accounts || []).length === 0) {
      toast.info('Add your first account, then drop your statements, we’ll match each one for you.');
      setAddAccountOpen(true);
      return;
    }
    const incoming = Array.from(files).filter((f) => /\.pdf$/i.test(f.name) || f.type === 'application/pdf');
    if (incoming.length === 0) {
      toast.error('Bulk backfill accepts PDFs only. For CSVs use the Single statement tab.');
      return;
    }
    const available = effectiveMax - queue.length;
    if (available <= 0) {
      const reason = pdfRemaining != null && pdfRemaining < maxFiles
        ? `Only ${pdfRemaining} PDF parse${pdfRemaining === 1 ? '' : 's'} remain this month.`
        : `You’ve reached the limit of ${maxFiles} PDFs ${trialCapped ? 'during your trial' : 'per batch'}.`;
      toast.error(reason);
      return;
    }
    const sliceIncoming = incoming.slice(0, available);

    // Phase 50, auto-detect account from filename. Many banks include the
    // account last-4 in the statement filename (e.g.
    // `Acct_Statement_XXXXXXXXX5382_07062026.pdf`). We pull the LAST run
    // of 4+ digits from the filename, take its trailing 4, and look for an
    // existing account with that last4. Best-effort, never blocks, only
    // assigns when there's a confident match.
    const detectAccountIdFromFilename = (filename) => {
      try {
        if (!filename || !accounts || accounts.length === 0) return null;
        const stem = String(filename).replace(/\.[^.]+$/, '');
        const runs = stem.match(/\d{4,}/g) || [];
        if (runs.length === 0) return null;
        const longest = runs.reduce((a, b) => (b.length >= a.length ? b : a), '');
        const candidate = longest.slice(-4);
        if (!/^\d{4}$/.test(candidate)) return null;
        const match = accounts.find((a) => (a.last4 || '').trim() === candidate);
        return match?.id || null;
      } catch (_) {
        return null;
      }
    };

    // Compute hash + duplicate-check for each file in parallel before adding
    // to the queue. Filename-detect happens here too, confident-match files
    // skip the peek entirely.
    //
    // Phase 51.1, resilience: a failure here (sha256 throws on a corrupt
    // File handle, dup-check network blip, etc.) used to drop the WHOLE
    // batch via Promise.all rejection. We now isolate per-file: any error
    // simply lands the file in the queue with no hash + no detection, in
    // 'undetected' state, the user can still pick an account and import.
    let autoDetectedCount = 0;
    const annotated = await Promise.all(sliceIncoming.map(async (file) => {
      let file_hash = null;
      let detectedId = null;
      let dup = null;
      try {
        file_hash = await sha256OfFile(file);
      } catch (e) {
        console.warn('sha256 failed (non-fatal):', file.name, e?.message || e);
      }
      try {
        detectedId = detectAccountIdFromFilename(file.name);
        if (detectedId) autoDetectedCount += 1;
      } catch (_) { /* keep detectedId null */ }
      if (file_hash) {
        try {
          const r = await checkDuplicate({
            token: session?.access_token,
            file_hash,
            account_id: detectedId || null,
            filename: file.name,
          });
          if (r?.duplicate) dup = r.match;
        } catch (e) {
          // checkDuplicate already swallows network errors, but defend
          // against unexpected throws so the batch survives.
          console.warn('checkDuplicate threw (non-fatal):', e?.message || e);
        }
      }
      return {
        id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        file_hash,
        file_size: file.size || null,
        // Phase 51, when filename match wins we're already in 'matched'.
        // Otherwise we start in 'peeking' so the row renders the calm
        // "reading account…" state immediately.
        account_id: detectedId || null,
        account_auto_detected: !!detectedId,
        account_status: detectedId ? 'matched' : 'peeking',
        detected_last4: null,
        detected_bank: null,
        status: 'queued',
        count: 0,
        error: null,
        dup,
      };
    }));
    if (incoming.length > available) {
      toast.warning(`Only ${available} more PDF${available === 1 ? '' : 's'} fit in this batch. The rest were skipped.`);
    }
    if (autoDetectedCount > 0) {
      toast.success(`Auto-matched ${autoDetectedCount} file${autoDetectedCount === 1 ? '' : 's'} from filename.`, { duration: 3500 });
    }
    setQueue((q) => [...q, ...annotated]);
    if (fileRef.current) fileRef.current.value = '';
    const dupCount = annotated.filter((it) => it.dup).length;
    if (dupCount > 0) {
      toast.warning(`${dupCount} of ${annotated.length} file${annotated.length === 1 ? '' : 's'} look${dupCount === 1 ? 's' : ''} like statements you’ve uploaded before, marked below.`, { duration: 7000 });
    }

    // Phase 51, Background peek pass for every file we couldn't match
    // by filename. Encrypted files now ALSO peek at drop-time: we try
    // every cached password silently before asking the user. This is
    // the magic for "12 same-bank statements = 1 password prompt".
    const needsPeek = annotated.filter((it) => !it.account_id);
    if (needsPeek.length === 0) return;

    // Process serially so password prompts don't pile up on the user.
    (async () => {
      for (const it of needsPeek) {
        try {
          const b64 = await fileToBase64(it.file);
          const enc = await isPdfEncrypted(it.file).catch(() => false);

          let peekData = null;

          if (enc) {
            // 1) Try every cached password silently.
            const cached = await tryPeekWithPasswordCache(it.file, b64);
            if (cached) {
              peekData = cached.data;
              // Remember the working password on this row so the
              // submission path doesn't have to re-discover it.
              updateItem(it.id, { pdf_password: cached.password });
            } else {
              // 2) None unlocked it → prompt the user ONCE for this file.
              //    Cache the answer so the rest of the batch tries it.
              //    Loop: re-prompt on bad password until success or cancel.
              let attemptErr = null;
              while (!peekData) {
                // eslint-disable-next-line no-await-in-loop
                const entered = await askForPdfPassword(it, attemptErr);
                if (!entered) {
                  // User cancelled, mark undetected so they can pick manually
                  // or remove the file.
                  updateItem(it.id, { account_status: 'undetected' });
                  break;
                }
                try {
                  // eslint-disable-next-line no-await-in-loop
                  const r = await axios.post(
                    `${BACKEND_URL}/api/import/peek-account`,
                    { file_base64: b64, filename: it.file.name, pdf_password: entered },
                    { headers: { Authorization: `Bearer ${session?.access_token}` }, timeout: 45000 },
                  );
                  if (r.data?.ok) {
                    peekData = r.data;
                    // Cache it for the rest of the batch + remember on this row.
                    if (!passwordCacheRef.current.includes(entered)) {
                      passwordCacheRef.current = [...passwordCacheRef.current, entered];
                    }
                    batchPasswordRef.current = entered;
                    updateItem(it.id, { pdf_password: entered });
                  } else if (r.data?.needs_password) {
                    // Backend says password didn't unlock the PDF, prompt again
                    // with the calm copy from the server (or our fallback).
                    // (Fixed in v2.7.8: backend returns {needs_password: true}
                    // not {reason: 'needs_password'}, the old check missed
                    // every wrong-password attempt and silently gave up.)
                    const serverMsg = (r.data?.detail && (r.data.detail.message || r.data.detail.error)) || null;
                    attemptErr = serverMsg || 'That password didn’t unlock this PDF. Try again.';
                  } else {
                    // Other failure, give up gracefully on peek.
                    updateItem(it.id, { account_status: 'undetected' });
                    break;
                  }
                } catch (_) {
                  attemptErr = 'We couldn’t read this statement. Try the password again.';
                }
              }
            }
          } else {
            // Non-encrypted → straight peek.
            try {
              const r = await axios.post(
                `${BACKEND_URL}/api/import/peek-account`,
                { file_base64: b64, filename: it.file.name, pdf_password: null },
                { headers: { Authorization: `Bearer ${session?.access_token}` }, timeout: 45000 },
              );
              if (r.data?.ok) peekData = r.data;
            } catch (_) { /* fall through to undetected */ }
          }

          if (!peekData) {
            // Mark undetected so the row leaves the 'peeking' state. If
            // the user already cancelled the password prompt we may have
            // already set 'undetected' above; updateItem is idempotent.
            updateItem(it.id, { account_status: 'undetected' });
            continue;
          }
          const last4 = peekData.last4 || null;
          const bank = peekData.bank_hint || null;
          if (last4) {
            const match = (accounts || []).find((a) => (a.last4 || '').trim() === last4);
            if (match) {
              updateItem(it.id, {
                account_id: match.id,
                account_auto_detected: true,
                account_status: 'matched',
                detected_last4: last4,
                detected_bank: bank,
              });
            } else {
              updateItem(it.id, {
                account_id: null,
                account_status: 'detected_new',
                detected_last4: last4,
                detected_bank: bank,
              });
            }
          } else {
            updateItem(it.id, { account_status: 'undetected' });
          }
        } catch (e) {
          updateItem(it.id, { account_status: 'undetected' });
        }
      }
    })();
  };

  // Allow the user to override the account assignment for any queue row.
  // When user picks manually we lock the row into 'matched' state and drop
  // any "auto-detected" framing so the UI shows a clean assignment.
  const setItemAccount = (id, account_id) => {
    if (running) return;
    setQueue((q) => q.map((x) => (
      x.id === id
        ? { ...x, account_id, account_status: 'matched', account_auto_detected: false }
        : x
    )));
  };

  // Phase 50, wrap context updaters with the local "running" guard for
  // user-initiated removals (we don't want the user yanking files mid-batch).
  // Context's raw setters are used by the async processOne path.
  const removeItem = (id) => {
    if (running) return;
    removeItemCtx(id);
  };
  const updateItem = updateItemCtx;

  // Phase 50, batchPasswordRef is now provided by BulkImportContext above.

  const processOne = async (item) => {
    updateItem(item.id, { status: 'reading' });

    // Phase 50, encrypted-PDF: prefer (1) any password already on the
    // queue item (set by per-file retry), (2) the batch password the user
    // gave us once at start. We only re-prompt if the server rejects.
    let pdfPassword = item.pdf_password || batchPasswordRef.current || null;
    // We deliberately do NOT prompt here, start() does the up-front
    // batch-level prompt for the whole queue.

    try {
      const res = await enqueueJob(item.file, {
        account_id: item.account_id || null,
        file_hash: item.file_hash || null,
        file_size: item.file_size != null ? item.file_size : (item.file.size || null),
        pdf_password: pdfPassword,
      });
      updateItem(item.id, { status: 'submitted', job_id: res.job_id });
      return { ok: true };
    } catch (e) {
      const status = e.response?.status;
      const serverDetail = e.response?.data?.detail;
      const errCode = (serverDetail && typeof serverDetail === 'object') ? serverDetail.error : null;
      const errMsg  = (serverDetail && typeof serverDetail === 'object') ? serverDetail.message : null;
      // Per-file fallback: server says this PDF needs / has a different
      // password than the batch-wide one. Ask the user, retry once.
      if (status === 401 && (errCode === 'pdf_password_required' || errCode === 'pdf_password_invalid')) {
        const entered = await askForPdfPassword(
          item,
          errCode === 'pdf_password_invalid' ? (errMsg || 'That password didn’t work. Try again.') : null,
        );
        if (entered) {
          updateItem(item.id, { pdf_password: entered });
          return processOne({ ...item, pdf_password: entered });
        }
        updateItem(item.id, { status: 'failed', error: 'Cancelled, password not provided for locked PDF.' });
        return { ok: false, error: 'Cancelled, password not provided for locked PDF.' };
      }
      let detail = (typeof serverDetail === 'string' ? serverDetail : null) || e.message || 'Could not submit PDF.';
      if (status === 429) detail = `Monthly PDF cap reached: ${detail}`;
      else if (status === 503) detail = `AI service unavailable: ${detail}`;
      else if (status === 413) detail = `PDF too large: ${detail}`;
      else if (status === 504) detail = 'This PDF took too long to unlock, try a shorter date range, or split the statement.';
      else if (e.code === 'ECONNABORTED' || /timeout/i.test(String(e.message || ''))) {
        // Client-side timeout, server didn't respond within 90s. Different
        // root cause from server-side 504; tell the user it's worth retrying.
        detail = 'Network timeout reading this PDF. Try again in a moment.';
      }
      updateItem(item.id, { status: 'failed', error: String(detail) });
      return { ok: false, error: String(detail) };
    }
  };

  // Phase 50, concurrency-limited runner. Submits up to N files in parallel
  // instead of waiting for each one to finish. The server itself accepts the
  // PDF and returns quickly (job_id), so 3 concurrent requests keep the
  // network busy without overwhelming the backend.
  const runWithConcurrency = async (items, limit, worker) => {
    const results = new Array(items.length);
    let cursor = 0;
    const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (true) {
        const i = cursor++;
        if (i >= items.length) return;
        // eslint-disable-next-line no-await-in-loop
        results[i] = await worker(items[i], i);
      }
    });
    await Promise.all(runners);
    return results;
  };

  const start = async () => {
    if (running || !queue.length) return;
    // Refuse to start if any queue item is missing an account assignment.
    const missingAcct = queue.filter((it) => !it.account_id);
    if (missingAcct.length) {
      toast.error(`${missingAcct.length} file${missingAcct.length === 1 ? '' : 's'}, pick an account for each before starting.`);
      return;
    }
    setRunning(true);
    setDoneSummary(null);
    const initial = queue.filter((it) => it.status !== 'submitted' && it.status !== 'done');

    // Phase 51, Password discovery now happens at drop time (see
    // handleAddFiles → peek-on-drop). Every encrypted file already has
    // its working password attached (`item.pdf_password`) and account
    // assignment resolved. We go straight to submission.
    //
    // The processOne fallback still catches edge cases (e.g. the user
    // assigned an account manually for an encrypted file we never asked
    // about, or the password changed between peek and parse).

    const failed = [];
    let submitted = 0;
    // Phase 50.1, backend now uses run_in_threadpool for CPU-bound work,
    // so 3 concurrent submissions are safe again and finish substantially
    // faster than 2.
    const results = await runWithConcurrency(initial, 3, async (it) => processOne(it));
    for (let i = 0; i < results.length; i++) {
      const r = results[i];
      const it = initial[i];
      if (r?.ok) submitted += 1;
      else {
        let errMsg;
        if (!r?.error) errMsg = 'Unknown error.';
        else if (typeof r.error === 'string') errMsg = r.error;
        else if (r.error?.message) errMsg = String(r.error.message);
        else { try { errMsg = JSON.stringify(r.error); } catch (_) { errMsg = String(r.error); } }
        failed.push({ name: it.file.name, error: errMsg });
      }
    }
    setDoneSummary({ submitted, failed });
    setRunning(false);
    refreshUsage();
    if (failed.length && submitted === 0) {
      toast.error(`Couldn’t queue any of the ${failed.length} file${failed.length === 1 ? '' : 's'}. Details below.`, { duration: 7000 });
    } else if (failed.length) {
      toast.warning(`Queued ${submitted} statement${submitted === 1 ? '' : 's'}. ${failed.length} failed.`, { duration: 7000 });
    } else {
      toast.success(`Queued ${submitted} statement${submitted === 1 ? '' : 's'}. We’ll notify you as each finishes.`);
    }
  };

  const reset = () => {
    // Phase 50, clearQueue() resets queue + doneSummary + batchPasswordRef
    // in the context provider in one go.
    clearQueue();
  };

  // ----- Render --------------------------------------------------------------
  // 1) Block free users entirely (PDF parsing isn't available)
  if (!canParsePdf) {
    return (
      <Card className="rounded-2xl border-border/70">
        <CardContent className="p-8 text-center space-y-3">
          <div className="inline-flex items-center gap-2 text-[11px] px-3 py-1 rounded-full bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold))] border border-[hsl(var(--gold))]/30">
            <Sparkles className="h-3.5 w-3.5" /> Available on Clarity and above
          </div>
          <div className="font-display text-xl font-semibold">Bulk backfill needs PDF parsing</div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">Pick up to 12 months of statements in one calm sweep. PDF reading is available on Clarity and above.</p>
          <Link to="/pricing"><Button className="rounded-xl" data-testid="bulk-upgrade-button">View plans <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="bulk-backfill-panel">
      {/* Header info card */}
      <Card className="rounded-2xl border-border/70 bg-card">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-xl bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold))] grid place-items-center shrink-0">
                <Files className="h-5 w-5" />
              </div>
              <div>
                <div className="font-display text-lg font-semibold">12-month backfill</div>
                <p className="text-sm text-muted-foreground max-w-xl">Drop up to {maxFiles} monthly statements in one batch. We&rsquo;ll read them, learn your patterns, and build snapshots + reflections for each month.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-[11px]">
              <Badge variant="outline" className="rounded-full px-2.5 py-0.5" data-testid="bulk-monthly-credits">
                Monthly Credits ({queue.length}/{maxFiles})
              </Badge>
              {poolActive ? (
                <Badge
                  variant="outline"
                  className="rounded-full px-2.5 py-0.5 border-[hsl(var(--gold))]/40 text-[hsl(var(--gold))]"
                  data-testid="bulk-onboarding-pool"
                  title="Onboarding setup-import pool"
                >
                  Setup Imports ({pool?.used ?? 0}/{pool?.total ?? 0})
                  {pool?.days_left != null ? ` · ${pool.days_left}d left` : ''}
                </Badge>
              ) : pdfCap != null && (
                <Badge
                  variant="outline"
                  className={`rounded-full px-2.5 py-0.5 ${capExhausted ? 'border-destructive/40 text-destructive' : 'border-border'}`}
                  data-testid="bulk-monthly-usage"
                  title="PDF parses used this calendar month"
                >
                  AI: {pdfUsed} / {pdfCap} this month
                </Badge>
              )}
              {/* Phase 49, Trial-limit badge removed entirely. The Monthly
                  Credits badge already conveys "X / Y this batch", and beta
                  users never see trial framing. */}
            </div>
          </div>

          {capExhausted && (
            <div className="mt-3 flex items-center gap-2 text-[11px] text-destructive rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2" data-testid="bulk-cap-exhausted">
              <AlertTriangle className="h-3.5 w-3.5" />
              <span>
                {poolActive
                  ? `You've used all ${pool?.total} setup imports. Your monthly cap of ${pdfCap} PDFs applies from here. `
                  : `You've used all ${pdfCap} PDF parses for this month. Resets on the 1st, or `}
                <Link to="/pricing" className="text-primary hover:underline">upgrade</Link> for more headroom.
              </span>
            </div>
          )}

          {poolActive && !capExhausted && (
            <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground rounded-xl border border-[hsl(var(--gold))]/30 bg-[hsl(var(--gold))]/8 px-3 py-2" data-testid="bulk-onboarding-banner">
              <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--gold))]" />
              <span>
                <strong className="text-foreground">Onboarding boost is active.</strong> Bring in your full financial history while it lasts , {poolRemaining} setup import{poolRemaining === 1 ? '' : 's'} left
                {pool?.days_left != null ? `, ${pool.days_left} day${pool.days_left === 1 ? '' : 's'} on the clock` : ''}.
              </span>
            </div>
          )}

          {trialCapped && (
            <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground rounded-xl border border-[hsl(var(--gold))]/30 bg-[hsl(var(--gold))]/8 px-3 py-2">
              <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--gold))]" />
              <span>During your trial, bulk backfill is capped at 4 months. Upgrade to bring 12 months in at once.</span>
              <Link to="/pricing" className="text-primary hover:underline ml-auto whitespace-nowrap">View plans</Link>
            </div>
          )}

          {/* Phase 50, calm batch tip. Drops the friction of per-file
              password prompts: same bank = same password. */}
          <div className="mt-3 flex items-start gap-2 text-[11px] text-muted-foreground rounded-xl border border-border/60 bg-muted/30 px-3 py-2" data-testid="bulk-tip-same-bank">
            <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--gold))] mt-0.5 shrink-0" />
            <span>
              <strong className="text-foreground">Tip:</strong> upload statements from the same bank in one batch, they usually share a password, so we&rsquo;ll ask for it once and reuse it across the whole batch.
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Upload zone OR cap-exhausted message */}
      {!doneSummary && capExhausted && (
        <Card className="rounded-2xl border-destructive/40 bg-destructive/5" data-testid="bulk-cap-card">
          <CardContent className="p-8 text-center space-y-3">
            <AlertTriangle className="h-7 w-7 text-destructive mx-auto" />
            <div className="font-display text-lg font-semibold">
              {poolActive ? 'Setup imports exhausted' : 'Monthly PDF parsing cap reached'}
            </div>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {poolActive
                ? `You've used all ${pool?.total} setup imports. Your monthly cap of ${pdfCap} PDFs still applies, switch to single imports, or top up your plan for more headroom.`
                : `You've used all ${pdfCap} PDF parses for this month${onTrial ? ' on your Horizon trial' : ''}. Resets on the 1st of next month, or upgrade for more headroom.`}
            </p>
            <Link to="/pricing"><Button className="rounded-xl" data-testid="bulk-cap-upgrade-button">View plans <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
          </CardContent>
        </Card>
      )}
      {!doneSummary && !capExhausted && accountsLoaded && (accounts || []).length === 0 && (
        <Card className="rounded-2xl border-border/70 bg-card" data-testid="bulk-no-accounts-card">
          <CardContent className="p-6 sm:p-7 flex flex-wrap items-center gap-4">
            <div className="h-10 w-10 rounded-xl bg-accent grid place-items-center shrink-0">
              <Wallet className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="font-display text-base font-semibold">Add your first account</div>
              <div className="text-[12px] text-muted-foreground">We&rsquo;ll auto-match every statement you drop to the right account, but we need at least one on file to start.</div>
            </div>
            <Button
              size="sm"
              className="rounded-xl"
              onClick={() => setAddAccountOpen(true)}
              data-testid="bulk-add-first-account-button"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Add an account
            </Button>
          </CardContent>
        </Card>
      )}
      {/* Phase 51, Default-account picker removed entirely. Account
          assignment is driven 100% by per-file detection
          (filename → content peek → user pick). */}
      {!doneSummary && !capExhausted && (
        <Card className="rounded-2xl border-border/70">
          <CardContent className="p-5 space-y-4">
            <label
              className={`block rounded-2xl border border-dashed border-border/70 bg-card hover:bg-accent/30 hover:border-primary/40 transition-colors p-8 text-center ${canAddMore ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
              data-testid="bulk-upload-dropzone"
            >
              <UploadCloud className="h-7 w-7 mx-auto text-muted-foreground" />
              <div className="mt-2 font-display text-base font-semibold">Drop statements here</div>
              <div className="text-xs text-muted-foreground">
                We&rsquo;ll read each one and match it to the right account , you confirm what we got wrong.
              </div>
              <div className="text-[11px] text-muted-foreground/80 mt-1">
                PDF only.
                {pdfRemaining != null && pdfRemaining < maxFiles
                  ? ` ${pdfRemaining} PDF parse${pdfRemaining === 1 ? '' : 's'} remaining this month.`
                  : ` Up to ${maxFiles} per batch.`}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                multiple
                disabled={!canAddMore}
                onChange={(e) => handleAddFiles(e.target.files)}
                data-testid="bulk-file-input"
              />
            </label>

            {/* Queue list */}
            {queue.length > 0 && (
              <div className="space-y-2" data-testid="bulk-queue-list">
                {queue.map((item) => {
                  const itemAccount = (accounts || []).find((a) => a.id === item.account_id);
                  // Phase 51, left accent stripe + tint communicate row
                  // state at a glance, without screaming. Calm green when
                  // matched; amber when a new account was detected; red
                  // when undetected and waiting on the user.
                  let ringCls = 'border-border/70';
                  if (item.status === 'failed') ringCls = 'border-destructive/40 bg-destructive/5';
                  else if (item.status === 'done' || item.status === 'submitted') ringCls = 'border-primary/30 bg-primary/[0.03]';
                  else if (item.account_status === 'matched') ringCls = 'border-emerald-500/30 bg-emerald-500/[0.04]';
                  else if (item.account_status === 'detected_new') ringCls = 'border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/[0.05]';
                  else if (item.account_status === 'undetected') ringCls = 'border-amber-500/40 bg-amber-500/[0.04]';
                  else if (item.account_status === 'peeking') ringCls = 'border-border/70 bg-muted/30';
                  if (item.dup) ringCls += ' ring-1 ring-[hsl(var(--gold))]/30';
                  return (
                    <div
                      key={item.id}
                      className={`rounded-xl border px-3.5 py-3 transition-colors ${ringCls}`}
                      data-testid={`bulk-row-${item.id}`}
                    >
                      {/* Line 1, filename + global submission status + remove */}
                      <div className="flex items-center gap-3">
                        <FileType2 className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate flex items-center gap-2">
                            <span className="truncate">{item.file.name}</span>
                            {item.dup && (
                              <Badge
                                variant="outline"
                                className="rounded-full text-[10px] px-1.5 py-0 border-[hsl(var(--gold))]/40 text-[hsl(var(--gold))] shrink-0"
                                title={`Looks like a previous upload from ${item.dup.created_at ? new Date(item.dup.created_at).toLocaleDateString() : 'earlier'}`}
                              >
                                possible duplicate
                              </Badge>
                            )}
                          </div>
                        </div>
                        <StatusBadge status={item.status} />
                        {(!running && item.status !== 'done') && (
                          <button
                            onClick={() => removeItem(item.id)}
                            className="text-muted-foreground hover:text-destructive transition-colors p-1 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            data-testid={`bulk-remove-${item.id}`}
                            aria-label="Remove from queue"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      {/* Line 2, account assignment state */}
                      <div className="mt-2 flex flex-wrap items-center gap-2 pl-7">
                        {/* PEEKING */}
                        {item.account_status === 'peeking' && (
                          <div className="text-[12px] text-muted-foreground inline-flex items-center gap-1.5">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Reading the statement to find the account&hellip;
                          </div>
                        )}

                        {/* MATCHED */}
                        {item.account_status === 'matched' && itemAccount && (
                          <>
                            <div className="text-[12px] inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400" data-testid={`bulk-row-matched-${item.id}`}>
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              <span className="font-medium text-foreground">{itemAccount.name}</span>
                              {itemAccount.last4 && (
                                <span className="text-muted-foreground tabular-nums">&bull;&bull;{itemAccount.last4}</span>
                              )}
                              {item.account_auto_detected && (
                                <span className="text-[10.5px] text-muted-foreground">&middot; auto-matched</span>
                              )}
                            </div>
                            {!running && item.status !== 'submitted' && item.status !== 'done' && (
                              <div className="ml-auto">
                                <RowAccountPicker
                                  value={item.account_id}
                                  accounts={accounts}
                                  onChange={(v) => setItemAccount(item.id, v)}
                                  onCreateClick={() => {
                                    setNewAccountPrefill({
                                      last4: item.detected_last4 || '',
                                      bank_hint: item.detected_bank || '',
                                      name: item.detected_bank
                                        ? `${item.detected_bank}${item.detected_last4 ? ` ••${item.detected_last4}` : ''}`
                                        : '',
                                    });
                                    setNewAccountForFile(item.id);
                                    setAddAccountOpen(true);
                                  }}
                                  triggerLabel="Change"
                                  variant="ghost"
                                  testid={`bulk-row-account-${item.id}`}
                                />
                              </div>
                            )}
                          </>
                        )}

                        {/* DETECTED NEW (no existing match) */}
                        {item.account_status === 'detected_new' && !running && (
                          <>
                            <div className="text-[12px] inline-flex items-center gap-1.5 text-[hsl(var(--gold))]">
                              <Sparkles className="h-3.5 w-3.5" />
                              <span className="font-medium text-foreground">
                                New account detected
                                {item.detected_last4 ? <> &bull;&bull;<span className="tabular-nums">{item.detected_last4}</span></> : null}
                                {item.detected_bank ? <span className="text-muted-foreground"> &middot; {item.detected_bank}</span> : null}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 ml-auto">
                              <Button
                                size="sm"
                                className="rounded-lg h-7 text-[11.5px] px-2.5"
                                data-testid={`bulk-row-create-account-${item.id}`}
                                onClick={() => {
                                  setNewAccountPrefill({
                                    last4: item.detected_last4 || '',
                                    bank_hint: item.detected_bank || '',
                                    name: item.detected_bank
                                      ? `${item.detected_bank} ••${item.detected_last4 || ''}`.trim()
                                      : `Account ••${item.detected_last4 || ''}`.trim(),
                                  });
                                  setNewAccountForFile(item.id);
                                  setAddAccountOpen(true);
                                }}
                              >
                                <Plus className="h-3.5 w-3.5 mr-1" /> Create account
                              </Button>
                              <RowAccountPicker
                                value={item.account_id}
                                accounts={accounts}
                                onChange={(v) => setItemAccount(item.id, v)}
                                onCreateClick={() => {
                                  setNewAccountPrefill({
                                    last4: item.detected_last4 || '',
                                    bank_hint: item.detected_bank || '',
                                    name: item.detected_bank
                                      ? `${item.detected_bank}${item.detected_last4 ? ` ••${item.detected_last4}` : ''}`
                                      : '',
                                  });
                                  setNewAccountForFile(item.id);
                                  setAddAccountOpen(true);
                                }}
                                triggerLabel="Pick existing"
                                variant="outline"
                                testid={`bulk-row-account-${item.id}`}
                              />
                            </div>
                          </>
                        )}

                        {/* UNDETECTED */}
                        {item.account_status === 'undetected' && !item.account_id && !running && (
                          <>
                            <div className="text-[12px] inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-500">
                              <AlertTriangle className="h-3.5 w-3.5" />
                              <span className="font-medium text-foreground">Couldn&rsquo;t auto-detect the account</span>
                              <span className="text-muted-foreground">, pick one for this file.</span>
                            </div>
                            <div className="ml-auto">
                              <RowAccountPicker
                                value={item.account_id}
                                accounts={accounts}
                                onChange={(v) => setItemAccount(item.id, v)}
                                onCreateClick={() => {
                                  setNewAccountPrefill(null);
                                  setNewAccountForFile(item.id);
                                  setAddAccountOpen(true);
                                }}
                                triggerLabel="Choose account"
                                variant="default"
                                testid={`bulk-row-account-${item.id}`}
                              />
                            </div>
                          </>
                        )}

                        {/* Post-submission status copy */}
                        {item.status === 'done' && (
                          <span className="text-[12px] text-muted-foreground">{item.count} transactions imported</span>
                        )}
                        {item.status === 'failed' && (
                          <span className="text-[12px] text-destructive truncate">{item.error}</span>
                        )}
                        {item.dup && !item.error && (
                          <span className="text-[11px] text-[hsl(var(--gold))] ml-auto">
                            Previously uploaded {item.dup.created_at ? new Date(item.dup.created_at).toLocaleDateString() : 'earlier'}{item.dup.txn_count ? ` · ${item.dup.txn_count} txns` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Progress + actions */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs text-muted-foreground">
                {running ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading statements&hellip;
                  </span>
                ) : queue.length === 0 ? (
                  <span>No files queued yet.</span>
                ) : (() => {
                  // Phase 51, live count of files still needing a decision.
                  const peeking = queue.filter((it) => it.account_status === 'peeking').length;
                  const needsPick = queue.filter((it) =>
                    !it.account_id && (it.account_status === 'detected_new' || it.account_status === 'undetected')
                  ).length;
                  const ready = queue.filter((it) => !!it.account_id).length;
                  if (peeking > 0) {
                    return (
                      <span className="inline-flex items-center gap-1.5">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Reading {peeking} statement{peeking === 1 ? '' : 's'}&hellip; {ready > 0 ? `${ready} ready` : ''}
                      </span>
                    );
                  }
                  if (needsPick > 0) {
                    return <span>{needsPick} file{needsPick === 1 ? '' : 's'} need{needsPick === 1 ? 's' : ''} an account , resolve below to start.</span>;
                  }
                  return <span>{queue.length} statement{queue.length === 1 ? '' : 's'} ready. Click Start when you are.</span>;
                })()}
              </div>
              <div className="flex items-center gap-2">
                {queue.length > 0 && !running && (
                  <Button variant="ghost" onClick={reset} className="rounded-xl" data-testid="bulk-clear-queue"><RotateCcw className="h-4 w-4 mr-1" /> Clear</Button>
                )}
                {(() => {
                  // Phase 51, Start is gated until every queued row has a
                  // resolved account. Rows still peeking or in 'detected_new'
                  // (waiting for Create) or 'undetected' without a manual
                  // pick must be resolved first.
                  const unresolved = queue.filter((it) =>
                    !it.account_id || it.account_status === 'peeking' || it.account_status === 'detected_new'
                  ).length;
                  const startDisabled = running || queue.length === 0 || unresolved > 0;
                  return (
                <Button
                  onClick={start}
                  disabled={startDisabled}
                  className="rounded-xl"
                  data-testid="bulk-start-button"
                  title={unresolved > 0 ? `${unresolved} file${unresolved === 1 ? '' : 's'} still need an account.` : undefined}
                >
                  {running
                    ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Reading&hellip;</>)
                    : unresolved > 0
                      ? (<><Sparkles className="h-4 w-4 mr-1" /> Resolve {unresolved} file{unresolved === 1 ? '' : 's'}</>)
                      : (<><Sparkles className="h-4 w-4 mr-1" /> Start backfill</>)}
                </Button>
                  );
                })()}
              </div>
            </div>

            {running && (
              <Progress
                value={Math.round(
                  (queue.filter((q) => q.status === 'done' || q.status === 'failed').length / Math.max(queue.length, 1)) * 100
                )}
                className="h-1.5"
                data-testid="bulk-progress-bar"
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Done summary, bulk submissions */}
      {doneSummary && (
        <Card className="rounded-2xl border-border/70" data-testid="bulk-done-card">
          <CardContent className="p-8 space-y-4">
            <div className="text-center space-y-2">
              {doneSummary.submitted > 0 ? (
                <CheckCircle2 className="h-8 w-8 text-primary mx-auto" />
              ) : (
                <AlertTriangle className="h-8 w-8 text-destructive mx-auto" />
              )}
              <div className="font-display text-xl font-semibold">
                {doneSummary.submitted > 0 ? 'Statements queued' : 'Nothing was queued'}
              </div>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                {doneSummary.submitted > 0
                  ? <>We&rsquo;re reading {doneSummary.submitted} statement{doneSummary.submitted === 1 ? '' : 's'} in the background. Each one will appear in the <Bell className="inline h-3.5 w-3.5 mx-0.5 -mt-0.5" /> bell when ready , tap to review and import.</>
                  : <>{(doneSummary.failed?.length || 0)} file{(doneSummary.failed?.length || 0) === 1 ? '' : 's'} could not be queued. Details below.</>}
              </p>
            </div>

            {/* Failure details */}
            {(doneSummary.failed?.length || 0) > 0 && (
              <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4 space-y-2" data-testid="bulk-failures-detail">
                <div className="flex items-center gap-2 text-xs font-medium">
                  <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                  Files we couldn&rsquo;t queue:
                </div>
                <div className="space-y-1.5">
                  {doneSummary.failed.map((f, i) => (
                    <div key={i} className="text-xs">
                      <div className="font-medium truncate">{f.name}</div>
                      <div className="text-muted-foreground break-words">{typeof f.error === 'string' ? f.error : (f.error?.message ? String(f.error.message) : 'Unknown error.')}</div>
                    </div>
                  ))}
                </div>
                {doneSummary.failed.some((f) => /cap|429|reached/i.test(f.error || '')) && (
                  <div className="pt-2 border-t border-destructive/20 text-[11px] text-muted-foreground">
                    Trial users get 4 PDF parses per month. <Link to="/pricing" className="text-primary hover:underline">View plans</Link> to lift the cap.
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-center gap-2 pt-2">
              <Button variant="secondary" onClick={reset} className="rounded-xl" data-testid="bulk-restart-button">Run another batch</Button>
              <Link to="/dashboard"><Button className="rounded-xl" data-testid="bulk-back-to-dashboard">Back to dashboard <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Phase 49, encrypted-PDF prompt for the bulk-backfill flow. */}
      <LockedPdfDialog
        open={!!lockedPdf}
        onOpenChange={(v) => { if (!v) onPasswordCancel(); }}
        filename={lockedPdf?.item?.file?.name}
        errorMessage={lockedPdf?.errorMessage}
        busy={!!lockedPdf?.busy}
        onSubmit={onPasswordSubmit}
        onCancel={onPasswordCancel}
      />

      {/* Phase 51, Inline new-account dialog. Always mounted so per-row
          "+ Create account" buttons (and the zero-state CTA) can open it. */}
      <NewAccountInlineDialog
        open={addAccountOpen}
        setOpen={(v) => { setAddAccountOpen(v); if (!v) setNewAccountPrefill(null); if (!v) setNewAccountForFile(null); }}
        defaults={newAccountPrefill}
        onCreated={async (newId) => {
          await reloadAccounts();
          if (newAccountForFile && newId) {
            updateItem(newAccountForFile, {
              account_id: newId,
              account_status: 'matched',
              account_auto_detected: false,
            });
            setNewAccountForFile(null);
          }
          setNewAccountPrefill(null);
        }}
      />
    </div>
  );
}

// Phase 84.3 slice-1 — StatusBadge, RowAccountPicker, AccountPickerCard,
// NewAccountInlineDialog, and DuplicateWarningDialog previously lived here
// as internal functions (~390 LOC). They have been extracted into
// /app/frontend/src/components/import/statements/ and are now imported
// at the top of this file. Behavior is unchanged.


// Default export, wraps the (renamed) inner component in the error
// boundary so a throw never blanks the page.
//
// Phase 84.2.1 — Optional `prefillFile` prop lets an outer surface (e.g.
// the adaptive Import Hub at /import) hand off a File that the user
// already dropped. The inner component will auto-trigger handleFile()
// exactly once when readiness conditions are met. `embedded` toggles
// chrome elements that only make sense on the standalone route.
export default function StatementImport({ prefillFile = null, embedded = false } = {}) {
  return (
    <_ImportErrorBoundary>
      <StatementImportInner prefillFile={prefillFile} embedded={embedded} />
    </_ImportErrorBoundary>
  );
}

